import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import EopReportModal from './reports/EopReportModal';
import { buildEopReportDescriptor } from '../utils/eopReportDescriptor';
import { reportingSnapshotFromCountItem, unitValuePriceFromCountItem } from '../utils/countItemReporting';
import { applyActiveFilter } from '../utils/sessionLifecycle';
import { formatUserCompact, shortId, getCurrentUser, isStoreOwnedBySuperAdmin, isSuperAdminId, SUPER_ADMIN_USER_IDS, COUNTERTOP_APP_CREATOR_LABEL } from '../utils/userDisplay';
import InventoryScheduleEditor from './InventoryScheduleEditor';
import PNLSetupWizard, { hasInProgressWizard } from './PNLSetupWizard';
import PNLCategoryMappingPanel, { checkPnlMappingStatus, loadCloverCategoryNamesByPnl } from './PNLCategoryMapping';
import EOPPosReportImport from './EOPPosReportImport';
import { buildPosApplyPlan, allocateProportionally } from '../utils/eopPosImport';
import {
  CALC_METHOD_ID,
  DATA_STATUS,
  computeCategoryRow,
  computeCogsPct,
  computeImpliedCostRate,
  computeAdjustedCostPct,
  parseOptionalAmount,
} from '../utils/eopMath';
import { buildPeriodEndOptions, nearestPeriodEndISO, previousPeriodEndISO, daysBetweenISO } from '../utils/eopPeriods';
import MobileReportPopup from './MobileReportPopup';
import { useIsMobile } from './MobileReportView';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatCurrency = (v) => currencyFormatter.format(Number.isFinite(v) ? v : 0);

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const parseNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const round2 = (n) => Math.round(n * 100) / 100;

// Dollar inputs hold strings; settle them into standard currency form —
// always exactly two decimals, rounded to the nearest penny ("0.5" → "0.50",
// "1.005" → "1.01"). Blank or unparseable stays blank.
const toMoneyString = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? (Math.round(n * 100) / 100).toFixed(2) : '';
};

const countDateFromSession = (s) => s?.completed_at || s?.session_date || s?.created_at || null;

const formatTime12h = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// ── Local-date ISO helpers (no UTC drift) ─────────────────────────────────────
const isoFromLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const isoToLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return Number.isNaN(dt.getTime()) ? null : dt;
};
const addDaysISO = (iso, n) => {
  const dt = isoToLocalDate(iso);
  if (!dt) return iso;
  dt.setDate(dt.getDate() + n);
  return isoFromLocalDate(dt);
};

// Counts that finish in the small hours (a late close, finishing just after
// midnight) really represent the PREVIOUS day's business. Roll those back so
// the Clover sales window stays aligned to the inventory period.
const EARLY_MORNING_CUTOFF_HOUR = 5;
const businessDateISOFromSession = (s) => {
  const raw = countDateFromSession(s);
  if (!raw) return null;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  if (dt.getHours() < EARLY_MORNING_CUTOFF_HOUR) dt.setDate(dt.getDate() - 1);
  return isoFromLocalDate(dt);
};

// ISO week number helper
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { week: Math.ceil((((d - yearStart) / 86400000) + 1) / 7), year: d.getUTCFullYear() };
}

// Auto-name generator (uses ending session as the period anchor)
function computeReportName(inventoryPeriodConfig, endSession) {
  if (!endSession) return '';
  const rawDate = countDateFromSession(endSession);
  if (!rawDate) return '';
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return '';

  const cfg = inventoryPeriodConfig;

  if (!cfg || !cfg.enabled || !cfg.schedule_type) {
    const shortDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${endSession.name || `Session ${endSession.id}`} EOP - ${shortDate}`;
  }

  const type = String(cfg.schedule_type);
  const countMs = date.getTime();

  if (type === 'month_end') {
    let bestDate = null;
    let bestDiff = Infinity;
    for (let offset = -1; offset <= 1; offset++) {
      const candidate = new Date(date.getFullYear(), date.getMonth() + 1 + offset, 0);
      const diff = Math.abs(countMs - candidate.getTime());
      if (diff < bestDiff) { bestDiff = diff; bestDate = candidate; }
    }
    const refDate = bestDate || date;
    const month = refDate.toLocaleDateString('en-US', { month: 'long' });
    return `${month} ${refDate.getFullYear()} EOP`;
  }

  if (type === 'weekly') {
    const weekEndDay = typeof cfg.week_end_day === 'number' ? cfg.week_end_day : 0;
    const countDow = date.getDay();
    let daysToEnd = (weekEndDay - countDow + 7) % 7;
    if (daysToEnd > 3) daysToEnd = daysToEnd - 7;
    const nearestBoundary = new Date(countMs + daysToEnd * 24 * 60 * 60 * 1000);
    const { week, year: wYear } = getISOWeek(nearestBoundary);
    return `Week ${week} ${wYear} EOP`;
  }

  if (type === '4-5-4' || type === '5-4-4' || type === 'custom_day' || type === 'fiscal') {
    const periodEndDates = Array.isArray(cfg.period_end_dates) ? cfg.period_end_dates : [];
    if (periodEndDates.length > 0) {
      let nearestIdx = 0;
      let nearestDiff = Infinity;
      for (let i = 0; i < periodEndDates.length; i++) {
        const ms = new Date(periodEndDates[i]).getTime();
        const diff = Math.abs(countMs - ms);
        if (diff < nearestDiff) { nearestDiff = diff; nearestIdx = i; }
      }
      const eopYear = new Date(periodEndDates[nearestIdx]).getFullYear();
      return `Period ${nearestIdx + 1} ${eopYear} EOP`;
    }
  }

  const shortDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${endSession.name || `Session ${endSession.id}`} EOP - ${shortDate}`;
}

const Toast = ({ message, type = 'success', onClose }) => (
  <div
    style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999,
      padding: '0.75rem 1.25rem', borderRadius: '10px',
      background: type === 'success' ? '#2c7a4b' : '#C0392B',
      color: '#fff', fontWeight: 600, fontSize: '0.9rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: 380,
    }}
  >
    <span style={{ flex: 1 }}>{message}</span>
    <button type="button" onClick={onClose}
      style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>
      ✕
    </button>
  </div>
);

const ConfirmDialog = ({ title, body, onConfirm, onCancel, confirmLabel = 'Confirm', confirmVariant = 'danger' }) => (
  <div role="dialog" aria-modal="true" style={{
    position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
    paddingLeft: '1rem', paddingRight: '1rem',
  }}>
    <div style={{
      background: '#fff', borderRadius: 14, padding: '1.75rem', maxWidth: 440, width: '100%',
      boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
    }}>
      <h5 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#3D2518', marginBottom: '0.65rem' }}>
        {title}
      </h5>
      <p style={{ color: '#5A4A42', fontSize: '0.92rem', marginBottom: '1.25rem' }}>{body}</p>
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className={`btn btn-${confirmVariant} btn-sm`} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

const InfoTip = ({ text }) => (
  <span
    title={text}
    aria-label={text}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: '50%', marginLeft: 4,
      background: '#E8DDD0', color: '#5A4A42', fontSize: '0.62rem',
      fontWeight: 700, cursor: 'help', verticalAlign: 'middle', flexShrink: 0,
    }}
  >
    i
  </span>
);

// Optional amount input — a blank field is treated as $0.00. Values settle
// to two decimals when the field loses focus.
const AmountCell = ({ value, onValueChange, ariaLabel }) => (
  <input
    type="number"
    className="form-control form-control-sm"
    placeholder="0.00"
    min="0"
    step="0.01"
    value={value || ''}
    onChange={(e) => onValueChange(e.target.value)}
    onBlur={() => {
      const settled = toMoneyString(value);
      if (settled !== (value || '')) onValueChange(settled);
    }}
    aria-label={ariaLabel}
  />
);

const IncompleteBadge = ({ categories, label = 'Incomplete' }) => (
  <span
    title={categories && categories.length
      ? `Missing data: ${categories.join(', ')}`
      : 'Some categories are missing data'}
    style={{
      fontSize: '0.62rem', marginLeft: 4, padding: '1px 6px', borderRadius: 6,
      background: '#FFF3CD', color: '#856404', fontWeight: 700, whiteSpace: 'nowrap',
      cursor: 'help',
    }}
  >
    {label}
  </span>
);

// Confirmation shown before generating when sales data was marked
// unavailable — distinct from the generic ConfirmDialog so the detail list
// renders as a proper list.
const LeaveWithoutApplyingDialog = ({ onConfirm, onCancel }) => (
  <div role="dialog" aria-modal="true" style={{
    position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
    paddingLeft: '1rem', paddingRight: '1rem',
  }}>
    <div style={{
      background: '#fff', borderRadius: 14, padding: '1.75rem', maxWidth: 460, width: '100%',
      boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
    }}>
      <h5 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#3D2518', marginBottom: '0.65rem' }}>
        Leave without applying your sales?
      </h5>
      <p style={{ color: '#5A4A42', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
        Do you wish to leave this step without applying your sales to the categories?
      </p>
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-warning btn-sm" onClick={onConfirm}>Yes</button>
      </div>
    </div>
  </div>
);

const MissingDataDialog = ({ items, onConfirm, onCancel }) => (
  <div role="dialog" aria-modal="true" style={{
    position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
    paddingLeft: '1rem', paddingRight: '1rem',
  }}>
    <div style={{
      background: '#fff', borderRadius: 14, padding: '1.75rem', maxWidth: 480, width: '100%',
      maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
    }}>
      <h5 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#3D2518', marginBottom: '0.65rem' }}>
        Some report data is incomplete.
      </h5>
      <ul style={{ color: '#5A4A42', fontSize: '0.88rem', margin: '0 0 0.9rem', paddingLeft: '1.2rem' }}>
        {items.map((it) => <li key={it}>{it}</li>)}
      </ul>
      <p style={{ color: '#5A4A42', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
        You can still generate an inventory-usage report. Sales-based and
        adjustment-based calculations that depend on missing data will be
        marked unavailable.
      </p>
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Return to Report</button>
        <button type="button" className="btn btn-warning btn-sm" onClick={onConfirm}>Generate with Missing Data</button>
      </div>
    </div>
  </div>
);

// Shown right after a POS report is applied when it only gave period totals
// for some deductions — most POS exports can't break discounts, comps, or
// refunds out by category. Forces a choice (enter category amounts manually
// or divide the totals by each category's share of sales) so those totals
// never linger as an unresolved "still needs information" state.
const PosAllocationDialog = ({ fields, canSplit, onManual, onSplit }) => {
  const list = Object.entries(fields)
    .map(([f, v]) => `${f} (${formatCurrency(v)})`)
    .join(', ');
  const optionStyle = {
    display: 'block', width: '100%', textAlign: 'left', background: '#FFFDF9',
    border: '1px solid #E0D5C8', borderRadius: 12, padding: '0.8rem 1rem', cursor: 'pointer',
  };
  return (
    <div role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      paddingLeft: '1rem', paddingRight: '1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '1.75rem', maxWidth: 500, width: '100%',
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
      }}>
        <h5 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#3D2518', marginBottom: '0.65rem' }}>
          How should these be allocated?
        </h5>
        <p style={{ color: '#5A4A42', fontSize: '0.9rem', marginBottom: '1.1rem' }}>
          Your report gives period totals for {list} but doesn't break them out by
          category — a limitation of most POS systems. Per-category amounts make
          this report the most accurate.
        </p>
        <button type="button" style={{ ...optionStyle, marginBottom: '0.6rem' }} onClick={onManual}>
          <div style={{ fontWeight: 700, color: '#3D2518', fontSize: '0.9rem' }}>I'll allocate them manually</div>
          <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginTop: 2 }}>
            The totals stay in Unallocated Adjustments — enter each category's
            amounts on the Discounts, Comps &amp; Waste step.
          </div>
        </button>
        <button
          type="button"
          style={{ ...optionStyle, opacity: canSplit ? 1 : 0.55, cursor: canSplit ? 'pointer' : 'not-allowed' }}
          disabled={!canSplit}
          onClick={onSplit}
        >
          <div style={{ fontWeight: 700, color: '#3D2518', fontSize: '0.9rem' }}>Split them in proportion to sales</div>
          <div style={{ fontSize: '0.78rem', color: '#8B7B73', marginTop: 2 }}>
            Each category takes the same share of the total as its share of sales —
            a category with 40% of sales absorbs 40% of the discounts.
            {!canSplit && ' (Unavailable until sales by category are entered.)'}
          </div>
        </button>
      </div>
    </div>
  );
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort, className = 'text-end' }) => {
  const active = sortConfig.key === sortKey;
  const arrow = active ? (sortConfig.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: active ? '#3D2518' : undefined }}
      onClick={() => onSort(sortKey)}
    >
      {label}{arrow}
    </th>
  );
};

const StepPill = ({ n, label, active, done, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'none', border: 'none', padding: '0.25rem 0.5rem',
      cursor: onClick ? 'pointer' : 'default',
      opacity: !active && !done && !onClick ? 0.55 : 1,
    }}
  >
    <span style={{
      width: 26, height: 26, borderRadius: '50%',
      background: active ? '#5A4A42' : (done ? '#2c7a4b' : '#D9CEC2'),
      color: '#fff', fontWeight: 700, fontSize: '0.78rem',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{done ? '✓' : n}</span>
    <span style={{
      fontSize: '0.82rem', fontWeight: active ? 700 : 600,
      color: active ? '#3D2518' : '#7A6259',
    }}>{label}</span>
  </button>
);

// Dynamic stepper: `steps` is the ordered list of { key, label } for THIS run
// of the wizard (the Category Mapping step is only present when mapping is
// actually required, so the visible numbering stays compact).
const Stepper = ({ steps, activeKey, completedKeys, onJump }) => {
  const activeIdx = steps.findIndex((s) => s.key === activeKey);
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
      background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 12,
      padding: '0.45rem 0.75rem', marginBottom: '1rem',
    }}>
      {steps.map((s, i) => {
        const done = completedKeys.has(s.key) && s.key !== activeKey;
        const jumpable = i < activeIdx || completedKeys.has(s.key);
        return (
          <React.Fragment key={s.key}>
            <StepPill
              n={i + 1}
              label={s.label}
              active={s.key === activeKey}
              done={done}
              onClick={jumpable && s.key !== activeKey ? () => onJump(s.key) : null}
            />
            {i < steps.length - 1 && (
              <span style={{ color: '#D9CEC2', fontSize: '0.85rem' }}>→</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Detect if a saved report contains legacy sales / COGS data.
// Reports written by the category-level workflow stamp `_v: 2` on every
// report_data row — those are never legacy, regardless of which sales fields
// they carry.
function isLegacyReport(report) {
  if (!report) return false;
  const rows = Array.isArray(report.report_data) ? report.report_data : [];
  if (rows.some((r) => r && Number(r._v) >= 2)) return false;
  if (report.report_type && report.report_type !== 'usage_only') return true;
  return rows.some((r) => r && (
    r.sales != null || r.discounts != null || r.comps != null ||
    r.recipe_cost_pct != null || r.net_sales != null || r.cogs_percent != null
  ));
}

// report_data rows that represent a P&L category (vs. the pseudo-row that
// carries unallocated adjustments).
const isCategoryRow = (r) => r && r.pnl_category_id != null && !r._row_type;
const findUnallocatedRow = (rows) =>
  (Array.isArray(rows) ? rows : []).find((r) => r && r._row_type === 'unallocated') || null;
// Pseudo-row that snapshots report-level calculated values (completeness,
// totals, calculation method) so historical reports don't change if source
// data is edited later.
const findMetaRow = (rows) =>
  (Array.isArray(rows) ? rows : []).find((r) => r && r._row_type === 'meta') || null;

const EOPWizard = ({ onNavigateToSettings, onBack, mobileOutputTitle = 'End of Period' }) => {
  const isMobile = useIsMobile();
  const [storeId, setStoreId] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [sessions, setSessions] = useState([]);
  const [userMap, setUserMap] = useState({});
  // True when the active store is owned by the super admin; gates whether the
  // admin's own name (vs the "Countertop App" label) shows on EOP reports.
  const [storeOwnedBySuperAdmin, setStoreOwnedBySuperAdmin] = useState(false);
  const [pnlCategories, setPnlCategories] = useState([]);
  const [hasCategories, setHasCategories] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [inventoryPeriodConfig, setInventoryPeriodConfig] = useState(null);

  // ── Clover integration state ──────────────────────────────────────
  const [cloverConnected, setCloverConnected] = useState(false);
  const [squareConnected, setSquareConnected] = useState(false);
  const [cloverSalesData, setCloverSalesData] = useState(null); // pulled result
  const [cloverPulling, setCloverPulling] = useState(false);
  const [cloverError, setCloverError] = useState('');
  const [cloverCategoryDiffs, setCloverCategoryDiffs] = useState([]); // [{ catId, prev, next }] on re-pull
  const [netSalesTotal, setNetSalesTotal] = useState(''); // USD string
  const [netSalesTotalSource, setNetSalesTotalSource] = useState(null); // 'clover' | 'manual' | null
  const [categorySalesOverrides, setCategorySalesOverrides] = useState({}); // { pnl_cat_id: string }
  const [categorySalesSource, setCategorySalesSource] = useState({}); // { pnl_cat_id: 'clover'|'manual' }
  // Editable sales-pull window (defaults proposed from the count dates; the
  // start is the day AFTER the starting count so the first day of the period
  // is included). Users can stretch it if they counted late.
  const [cloverPullStart, setCloverPullStart] = useState('');
  const [cloverPullEnd, setCloverPullEnd] = useState('');

  // ── POS report import state ───────────────────────────────────────
  // An end-of-period report uploaded from any POS (no API connection),
  // read by the eop-pos-import edge function and applied to the sales
  // fields. The extraction/mapping live here so they survive navigation.
  const [posExtraction, setPosExtraction] = useState(null); // normalized extraction
  const [posFileName, setPosFileName] = useState('');
  const [posMapping, setPosMapping] = useState({}); // { categoryIdx: pnlCatId | '' }
  const [posApplied, setPosApplied] = useState(null); // snapshot of what was written
  const [posAllocationPrompt, setPosAllocationPrompt] = useState(null); // { fields: { comps: 4215.6, … } } | null

  const [activeTab, setActiveTab] = useState('wizard');

  // ── Stepped wizard state (key-based) ──────────────────────────────
  // The Category Mapping step is only inserted when mapping is incomplete,
  // so the visible step numbering is derived from the active step list.
  const [stepKey, setStepKey] = useState('period');
  const [completedKeys, setCompletedKeys] = useState(() => new Set());

  // ── Category-mapping status ───────────────────────────────────────
  const [mappingRequired, setMappingRequired] = useState(false);
  const [mappingUnmapped, setMappingUnmapped] = useState({ inventory: [], clover: [] });
  const [sessionExtraCategories, setSessionExtraCategories] = useState([]);

  // ── Category-level report inputs (keyed by String(pnl_category_id)) ─
  const [transfersInByCat, setTransfersInByCat] = useState({});
  const [transfersOutByCat, setTransfersOutByCat] = useState({});
  const [adjustmentsByCat, setAdjustmentsByCat] = useState({});
  const [adjustmentNotesByCat, setAdjustmentNotesByCat] = useState({});
  const [grossSalesByCat, setGrossSalesByCat] = useState({});
  const [grossSalesSourceByCat, setGrossSalesSourceByCat] = useState({}); // 'clover' | 'manual'
  const [overrideUnlockedByCat, setOverrideUnlockedByCat] = useState({}); // unlocked via "Edit imported amounts"
  const [overrideNoteByCat, setOverrideNoteByCat] = useState({});
  const [discountsByCat, setDiscountsByCat] = useState({});
  const [compsByCat, setCompsByCat] = useState({});
  const [refundsByCat, setRefundsByCat] = useState({});
  const [wasteByCat, setWasteByCat] = useState({});
  const [missingDataConfirm, setMissingDataConfirm] = useState(null); // { items: [] } | null
  const [unallocated, setUnallocated] = useState({ discounts: '', comps: '', refunds: '', waste: '' });
  const [salesUnavailable, setSalesUnavailable] = useState(false);
  const [salesMode, setSalesMode] = useState(null); // 'clover' | 'clover-adjust' | 'manual' (Clover stores only)
  const [cloverCatNamesByPnl, setCloverCatNamesByPnl] = useState({});
  const [negativeNetConfirmed, setNegativeNetConfirmed] = useState(false);

  // ── Reporting period: "which period are you closing?" ─────────────
  // The dropdown offers period ends from the store's accounting calendar
  // (4-5-4 / 5-4-4 / weekly / custom schedules use their configured dates;
  // otherwise calendar month ends) plus a custom date. The end nearest to
  // today is pre-selected — on June 3rd we assume May is being closed.
  const [periodEndChoice, setPeriodEndChoice] = useState(null); // ISO date | 'custom' | null
  const [customPeriodEnd, setCustomPeriodEnd] = useState('');
  const [periodChoiceTouched, setPeriodChoiceTouched] = useState(false);

  const editingRef = useRef(null);
  const sessionISODate = (s) => {
    const raw = countDateFromSession(s);
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Selection: beginning (green) and ending (brown) roles are tracked
  // explicitly so the marker drag-and-drop can move each role independently.
  const [startSessionId, setStartSessionId] = useState(null);
  const [endSessionId, setEndSessionId] = useState(null);
  const [autoSelectApplied, setAutoSelectApplied] = useState(false);
  const [draggingRole, setDraggingRole] = useState(null); // 'start' | 'end' | null
  const [showAllCounts, setShowAllCounts] = useState(false); // "Choose Other" full list
  const [countsConfirm, setCountsConfirm] = useState(null); // { body, onConfirm } | null

  const [purchases, setPurchases] = useState({});
  const [inventoryValues, setInventoryValues] = useState({});
  const [loadingInventory, setLoadingInventory] = useState(false);

  const [reportName, setReportName] = useState('');
  const [reportNameEdited, setReportNameEdited] = useState(false);

  // ── Mapping check / first-time setup state ────────────────────────
  // The guided P&L setup modal is kept ONLY for stores with no P&L
  // categories at all; routine mapping gaps are handled by the inline
  // Category Mapping step.
  const [mappingCheckLoading, setMappingCheckLoading] = useState(false);
  const [showPNLWizard, setShowPNLWizard] = useState(false);

  // ── Edit / legacy state ───────────────────────────────────────────
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingLegacyReport, setEditingLegacyReport] = useState(null); // raw report for preserving fields
  const [editConfirmStep, setEditConfirmStep] = useState(0);
  const [pendingEditReport, setPendingEditReport] = useState(null);

  // ── Save state ────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [savedReportId, setSavedReportId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  // Confirm before leaving the POS category-match step with an uploaded report
  // that hasn't been applied to the P&L categories yet.
  const [posLeaveConfirm, setPosLeaveConfirm] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // ── History state ─────────────────────────────────────────────────
  const [eopHistory, setEopHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeHistoryRow, setActiveHistoryRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'report_date', dir: 'desc' });

  // ── Schedule editor visibility ────────────────────────────────────
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);

  useEffect(() => { editingRef.current = editingReportId; }, [editingReportId]);

  // ── Period options & derived dates ─────────────────────────────────
  const periodOptions = useMemo(
    () => buildPeriodEndOptions(inventoryPeriodConfig, todayISO()),
    [inventoryPeriodConfig],
  );

  // Pre-select the period end nearest to today once options exist; the
  // user's own choice is never overwritten (periodChoiceTouched), and an
  // edited report keeps its hydrated selections.
  useEffect(() => {
    if (periodChoiceTouched || editingRef.current) return;
    if (!periodOptions.length) return;
    const nearest = nearestPeriodEndISO(periodOptions, todayISO());
    if (nearest) setPeriodEndChoice(nearest);
  }, [periodOptions, periodChoiceTouched]);

  // The chosen period-close date (ISO) — from the dropdown or the custom input.
  const periodEndDate = periodEndChoice === 'custom'
    ? (customPeriodEnd || null)
    : (periodEndChoice || null);

  // The previous boundary in the store's counting structure — the target
  // date for the beginning inventory.
  const prevPeriodEndDate = useMemo(
    () => (periodEndDate ? previousPeriodEndISO(inventoryPeriodConfig, periodEndDate) : null),
    [periodEndDate, inventoryPeriodConfig],
  );

  // Changing the period invalidates the previous auto-selection so the
  // counts step re-picks against the new close date.
  useEffect(() => {
    if (editingRef.current) return;
    setAutoSelectApplied(false);
  }, [periodEndDate]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    const sid = Number(localStorage.getItem('selectedStoreId'));
    if (!sid) {
      setError('No store selected. Please select a store first.');
      setLoadingSessions(false);
      return;
    }
    setStoreId(sid);
    loadSessions(sid);
    loadPnlCategories(sid);
    loadEopHistory(sid);
    loadStoreDetails(sid);
    checkCloverConnection(sid);
    checkSquareConnection(sid);
  }, []);

  const loadStoreDetails = async (sid) => {
    try {
      const { data } = await supabase
        .from('stores')
        .select('name, inventory_period_config')
        .eq('id', sid)
        .maybeSingle();
      if (data?.inventory_period_config) setInventoryPeriodConfig(data.inventory_period_config);
      if (data?.name) {
        setStoreName(data.name);
        try { localStorage.setItem('selectedStoreName', data.name); } catch (_e) {}
      }
    } catch (_e) {}
  };

  // Restaurant name for the report hero header, with a localStorage fallback so
  // the report still renders before the stores row resolves.
  const reportStoreName = storeName
    || (() => { try { return localStorage.getItem('selectedStoreName') || ''; } catch (_e) { return ''; } })()
    || 'Your Restaurant';

  const checkCloverConnection = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const { data } = await supabase
        .from('clover_connections')
        .select('status')
        .eq('store_id', sid)
        .eq('status', 'active')
        .maybeSingle();
      setCloverConnected(!!data);
    } catch (_e) {
      setCloverConnected(false);
    }
  }, []);

  const checkSquareConnection = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const { data } = await supabase
        .from('square_connections')
        .select('status')
        .eq('store_id', sid)
        .eq('status', 'active')
        .maybeSingle();
      setSquareConnected(!!data);
    } catch (_e) {
      setSquareConnected(false);
    }
  }, []);

  // Resolved begin/end sessions (declared before the Clover callbacks that
  // depend on them to avoid a temporal-dead-zone reference error).
  const beginSession = useMemo(() => {
    if (!startSessionId) return null;
    return sessions.find((s) => String(s.id) === String(startSessionId)) || null;
  }, [startSessionId, sessions]);
  const endSession = useMemo(() => {
    if (!endSessionId) return null;
    return sessions.find((s) => String(s.id) === String(endSessionId)) || null;
  }, [endSessionId, sessions]);
  const orderedSelection = useMemo(() => {
    if (!beginSession || !endSession) return null;
    return { begin: beginSession, end: endSession };
  }, [beginSession, endSession]);

  // Compatibility shim: existing checks count "selectedSessionIds.length === 2"
  // to mean "user picked both ends." Derive it from start/end roles.
  const selectedSessionIds = useMemo(() => {
    const ids = [];
    if (startSessionId) ids.push(String(startSessionId));
    if (endSessionId && String(endSessionId) !== String(startSessionId)) ids.push(String(endSessionId));
    return ids;
  }, [startSessionId, endSessionId]);

  const pullCloverSales = useCallback(async () => {
    if (!storeId || !beginSession || !endSession) return;
    // Use the sales window derived from the selected inventory counts.
    const periodStart = cloverPullStart;
    const periodEnd = cloverPullEnd;
    if (!periodStart || !periodEnd) {
      setCloverError('Set the sales period start and end dates first.');
      return;
    }
    if (periodStart > periodEnd) {
      setCloverError('The sales period start must be on or before the end date.');
      return;
    }
    setCloverPulling(true);
    setCloverError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const resp = await fetch(
        `${SUPABASE_URL}/functions/v1/clover-sales-pull`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_KEY,
          },
          body: JSON.stringify({
            store_id: storeId,
            period_start: periodStart,
            period_end: periodEnd,
          }),
        },
      );
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || 'Failed to pull Clover sales.');

      // Detect changes if we already had data
      const prevData = cloverSalesData;
      setCloverSalesData(result);

      // Keep the Clover net-sales total as a reference figure unless the user
      // typed their own.
      if (netSalesTotalSource !== 'manual') {
        setNetSalesTotal(String(result.net_sales_total));
        setNetSalesTotalSource('clover');
      }

      // Pre-fill per-category GROSS sales where Clover has a mapped value.
      // (Clover's per-category figures are line-item revenue before discounts,
      // so they feed the Gross Sales column.) Categories the user explicitly
      // overrode are left alone.
      if (result.category_sales && typeof result.category_sales === 'object') {
        setGrossSalesByCat((prev) => {
          const next = { ...prev };
          Object.entries(result.category_sales).forEach(([catId, amount]) => {
            if (grossSalesSourceByCat[catId] !== 'manual') {
              next[catId] = toMoneyString(amount);
            }
          });
          return next;
        });
        setGrossSalesSourceByCat((prev) => {
          const next = { ...prev };
          Object.entries(result.category_sales).forEach(([catId]) => {
            if (prev[catId] !== 'manual') next[catId] = 'clover';
          });
          return next;
        });
      }

      // Pre-fill per-category DISCOUNTS from Clover. The engine pulls every
      // discount (order- and line-level, amount or percentage), allocates
      // order-level discounts across categories in proportion to sales, and
      // returns the per-P&L-category total. These are locked/read-only for
      // Clover stores — the operator never enters discounts manually when the
      // POS supplies them. Comps and refunds remain manual.
      if (result.category_discounts && typeof result.category_discounts === 'object') {
        setDiscountsByCat((prev) => {
          const next = { ...prev };
          Object.entries(result.category_discounts).forEach(([catId, amount]) => {
            next[catId] = toMoneyString(amount);
          });
          return next;
        });
      }

      // Show diff on re-pull: net sales + per-category changes
      if (prevData) {
        const netDiff = result.net_sales_total - prevData.net_sales_total;
        // Compute per-category diffs
        const allCatIds = new Set([
          ...Object.keys(prevData.category_sales || {}),
          ...Object.keys(result.category_sales || {}),
        ]);
        const catDiffs = [];
        for (const catId of allCatIds) {
          const prev = Number(prevData.category_sales?.[catId] ?? 0);
          const next = Number(result.category_sales?.[catId] ?? 0);
          if (Math.abs(prev - next) >= 0.01) catDiffs.push({ catId, prev, next });
        }
        setCloverCategoryDiffs(catDiffs);
        if (Math.abs(netDiff) >= 0.01) {
          const sign = netDiff > 0 ? '+' : '';
          showToast(
            `Net sales updated: ${sign}${formatCurrency(netDiff)}${catDiffs.length ? ` · ${catDiffs.length} categor${catDiffs.length === 1 ? 'y' : 'ies'} changed` : ''}`,
            netDiff >= 0 ? 'success' : 'error',
          );
        } else if (catDiffs.length > 0) {
          showToast(`${catDiffs.length} categor${catDiffs.length === 1 ? 'y' : 'ies'} changed since last pull.`, 'success');
        } else {
          showToast('Re-pulled — no changes since last pull.', 'success');
        }
      } else {
        setCloverCategoryDiffs([]);
        showToast('Clover sales data pulled successfully.', 'success');
      }
    } catch (e) {
      setCloverError(e.message || 'Failed to pull sales from Clover.');
    } finally {
      setCloverPulling(false);
    }
  }, [storeId, beginSession, endSession, cloverPullStart, cloverPullEnd, cloverSalesData, netSalesTotalSource, grossSalesSourceByCat, showToast]);

  // ── Propose the sales window from the selected counts ────────────────
  // Start = day AFTER the beginning count's business date (so the first day of
  // the new period is included); End = the ending count's business date.
  const proposedPullStart = useMemo(
    () => (beginSession ? addDaysISO(businessDateISOFromSession(beginSession), 1) : ''),
    [beginSession],
  );
  const proposedPullEnd = useMemo(
    () => (endSession ? businessDateISOFromSession(endSession) : ''),
    [endSession],
  );
  useEffect(() => {
    setCloverPullStart(proposedPullStart || '');
    setCloverPullEnd(proposedPullEnd || '');
  }, [proposedPullStart, proposedPullEnd]);

  const loadSessions = async (sid) => {
    setLoadingSessions(true);
    try {
      const { data, error: sessErr } = await applyActiveFilter(supabase
        .from('count_sessions')
        .select('id, name, session_date, status, completed_at, items_counted, total_items, count_group_id, created_at, count_purpose, inventory_mode, template_name_snapshot, template_type_snapshot, created_by')
        .eq('store_id', sid))
        .order('completed_at', { ascending: false, nullsFirst: false });
      if (sessErr) throw sessErr;
      const completed = (data || []).filter((s) => s.status === 'completed');
      setSessions(completed);

      // Records the super admin created in a store they don't own read as
      // "Countertop App" instead of the admin's name.
      const adminOwnsStore = await isStoreOwnedBySuperAdmin(supabase, sid);
      setStoreOwnedBySuperAdmin(adminOwnsStore);

      // Resolve "counted by" display names from user_profiles for the session picker.
      const uids = [...new Set(completed.map((s) => s.created_by).filter(Boolean))];
      if (uids.length > 0) {
        supabase
          .from('user_profiles')
          .select('id, display_name, first_name, last_name, email')
          .in('id', uids)
          .then(({ data: profiles }) => {
            const map = {};
            (profiles || []).forEach((p) => {
              map[p.id] = formatUserCompact(p) || p.email || shortId(p.id);
            });
            if (!adminOwnsStore) {
              SUPER_ADMIN_USER_IDS.forEach((id) => { map[id] = COUNTERTOP_APP_CREATOR_LABEL; });
            }
            setUserMap(map);
          })
          .catch(() => {});
      } else {
        setUserMap({});
      }
      return completed;
    } catch (e) {
      setError(e.message || 'Failed to load sessions.');
      return [];
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadPnlCategories = async (sid) => {
    try {
      const { data, error: catErr } = await supabase
        .from('pnl_categories')
        .select('id, name, sort_order')
        .eq('store_id', sid)
        .order('sort_order', { ascending: true });
      if (catErr) throw catErr;
      setPnlCategories(data || []);
      setHasCategories((data || []).length > 0);
      return data || [];
    } catch (_e) {
      setHasCategories(false);
      return [];
    }
  };

  const loadEopHistory = async (sid) => {
    setLoadingHistory(true);
    try {
      const { data, error: histErr } = await supabase
        .from('pnl_reports')
        .select(`
          id, report_date, period_label, report_type, start_session_id, end_session_id,
          count_group_name, actual_usage_total, theoretical_usage_total, variance_dollars_total,
          variance_pct_total, aggregate_cogs_pct, net_sales_total,
          report_data, clover_sales_audit, created_at, updated_at
        `)
        .eq('store_id', sid)
        .order('report_date', { ascending: false });
      if (histErr) throw histErr;
      setEopHistory(data || []);
      return data || [];
    } catch (e) {
      setError(e.message || 'Failed to load history.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadMappings = async (sid, catIds) => {
    if (!catIds.length) return new Map();
    const { data } = await supabase
      .from('pnl_category_mappings')
      .select('pnl_category_id, inventory_category')
      .eq('store_id', sid)
      .in('pnl_category_id', catIds);
    const map = new Map();
    (data || []).forEach((m) => {
      const list = map.get(m.pnl_category_id) || [];
      list.push((m.inventory_category || '').toLowerCase());
      map.set(m.pnl_category_id, list);
    });
    return map;
  };

  const calcInventoryTotalsForSession = useCallback(async (sessionId, mappings) => {
    const { data: items } = await supabase
      .from('count_items')
      .select('actual_quantity, item_snapshot, snapshot_at_close')
      .eq('count_session_id', sessionId);
    const totals = {};
    (items || []).forEach((row) => {
      const snap = reportingSnapshotFromCountItem(row);
      const cat = (snap.category || '').toLowerCase().trim();
      const qty = parseFloat(row.actual_quantity) || 0;
      const price = unitValuePriceFromCountItem(row);
      mappings.forEach((invCats, pnlCatId) => {
        if (invCats.includes(cat)) {
          totals[pnlCatId] = (totals[pnlCatId] || 0) + qty * price;
        }
      });
    });
    return totals;
  }, []);

  // Distinct inventory subcategories present across the two selected sessions
  const fetchDistinctCategoriesForSessions = useCallback(async (sessionIds) => {
    if (!sessionIds || !sessionIds.length) return [];
    const { data } = await supabase
      .from('count_items')
      .select('item_snapshot, snapshot_at_close')
      .in('count_session_id', sessionIds.map(Number));
    const set = new Set();
    (data || []).forEach((row) => {
      const snap = reportingSnapshotFromCountItem(row);
      const cat = (snap.category || '').trim();
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, []);

  // ── Derived: counts eligible for end-of-period reporting ──────────
  // No date filtering — the auto-pick targets the chosen period close, and
  // "Choose Other" exposes the entire history (newest first).
  const eligibleSessions = useMemo(() => {
    return sessions.filter((s) => {
      // Order counts are not eligible for end-of-period reporting.
      if (s.order_calculations_enabled === true) return false;
      if ((s.count_purpose || '') === 'ordering') return false;
      return !!countDateFromSession(s);
    });
  }, [sessions]);

  // "Choose Other" display order: the assigned counts first (Beginning, then
  // Ending) so the current selection is obvious, then everything else newest
  // first. Re-sorts immediately on swap or reassignment.
  const displayCounts = useMemo(() => {
    const rolePriority = (s) => {
      const idStr = String(s.id);
      if (String(startSessionId || '') === idStr) return 0;
      if (String(endSessionId || '') === idStr) return 1;
      return 2;
    };
    return [...eligibleSessions].sort((a, b) => {
      const pa = rolePriority(a);
      const pb = rolePriority(b);
      if (pa !== pb) return pa - pb;
      const aMs = new Date(countDateFromSession(a) || 0).getTime();
      const bMs = new Date(countDateFromSession(b) || 0).getTime();
      return bMs - aMs;
    });
  }, [eligibleSessions, startSessionId, endSessionId]);

  // beginSession / endSession / orderedSelection / selectedSessionIds are
  // declared earlier (above pullCloverSales) so the Clover callbacks can depend
  // on them without hitting a temporal-dead-zone error.

  // ── Auto-select the counts aligned with the chosen period close ────
  // Ending = completed count whose business date sits closest to the close
  // date (a count finished just after midnight still belongs to the close
  // day). Beginning = the count closest to the PREVIOUS boundary in the
  // store's counting structure. "Choose Other" can override either freely.
  useEffect(() => {
    if (stepKey !== 'counts') return;
    if (autoSelectApplied) return;
    if (editingRef.current) return; // never override a saved-report selection
    if (!periodEndDate) return;
    if (eligibleSessions.length === 0) return;

    const dated = eligibleSessions
      .map((s) => ({ id: String(s.id), iso: businessDateISOFromSession(s) }))
      .filter((x) => !!x.iso);
    if (dated.length === 0) return;

    const closestTo = (candidates, targetISO) => {
      if (!targetISO) return null;
      let best = null;
      let bestDist = Infinity;
      candidates.forEach((x) => {
        const dist = Math.abs(daysBetweenISO(x.iso, targetISO) ?? Infinity);
        if (dist < bestDist) { bestDist = dist; best = x; }
      });
      return best;
    };

    const endPick = closestTo(dated, periodEndDate);
    // The beginning must not come after the ending — a count later than the
    // ending belongs to the next period, so it's never auto-assigned
    // backwards. When no earlier count exists, the beginning is left
    // unselected and the user picks one via Choose Other.
    const startCandidates = endPick
      ? dated.filter((x) => x.id !== endPick.id && x.iso <= endPick.iso)
      : dated;
    const startPick = closestTo(startCandidates, prevPeriodEndDate);
    setEndSessionId(endPick ? endPick.id : null);
    setStartSessionId(startPick ? startPick.id : null);
    setAutoSelectApplied(true);
  }, [stepKey, periodEndDate, prevPeriodEndDate, eligibleSessions, autoSelectApplied]);

  // ── Drag & drop / swap helpers ────────────────────────────────────
  const applyAssignment = useCallback((nextStart, nextEnd) => {
    setStartSessionId(nextStart);
    setEndSessionId(nextEnd);
    setAutoSelectApplied(true);
  }, []);

  // Date-sanity issues (ending before beginning, dates suspiciously close)
  // are confirmed once when leaving the step — see advanceFromCounts.
  const finalizeAssignment = useCallback((nextStart, nextEnd) => {
    if (nextStart && nextEnd && String(nextStart) === String(nextEnd)) return;
    applyAssignment(nextStart, nextEnd);
  }, [applyAssignment]);

  const requestAssignment = useCallback((role, sessionId) => {
    const idStr = String(sessionId);
    let nextStart = startSessionId;
    let nextEnd = endSessionId;
    if (role === 'start') {
      if (idStr === String(endSessionId)) {
        // Dropped start onto current end card → swap roles between them.
        nextStart = idStr;
        nextEnd = startSessionId;
      } else {
        nextStart = idStr;
      }
    } else if (role === 'end') {
      if (idStr === String(startSessionId)) {
        nextEnd = idStr;
        nextStart = endSessionId;
      } else {
        nextEnd = idStr;
      }
    }
    finalizeAssignment(nextStart, nextEnd);
  }, [startSessionId, endSessionId, finalizeAssignment]);

  const handleSwap = useCallback(() => {
    if (!startSessionId || !endSessionId) return;
    finalizeAssignment(endSessionId, startSessionId);
  }, [startSessionId, endSessionId, finalizeAssignment]);

  // Auto-name when end session resolves
  useEffect(() => {
    if (reportNameEdited) return;
    if (!endSession) return;
    setReportName(computeReportName(inventoryPeriodConfig, endSession));
  }, [endSession?.id, inventoryPeriodConfig, reportNameEdited]);

  // Steps that need inventory/category data loaded (anything past the picker).
  const DATA_STEPS = ['purchases', 'sales', 'adjustments', 'review'];

  // Re-load inventory values whenever the two sessions or PNL categories change
  // (and we're past the picker step). Skip if editing a legacy report from store.
  useEffect(() => {
    if (!storeId || !beginSession || !endSession || !pnlCategories.length) return;
    if (!DATA_STEPS.includes(stepKey)) return;
    let cancelled = false;
    (async () => {
      setLoadingInventory(true);
      try {
        const mappings = await loadMappings(storeId, pnlCategories.map((c) => c.id));
        const [beginTotals, endTotals] = await Promise.all([
          calcInventoryTotalsForSession(Number(beginSession.id), mappings),
          calcInventoryTotalsForSession(Number(endSession.id), mappings),
        ]);
        if (cancelled) return;
        const vals = {};
        pnlCategories.forEach((cat) => {
          vals[cat.id] = {
            beginning: beginTotals[cat.id] || 0,
            ending: endTotals[cat.id] || 0,
          };
        });
        setInventoryValues(vals);
      } catch (_e) {
        if (!cancelled) setError('Failed to load inventory values. Check your P&L category mappings.');
      } finally {
        if (!cancelled) setLoadingInventory(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beginSession?.id, endSession?.id, storeId, pnlCategories, stepKey]);

  // "Clover Categories Included" names for the Sales by Category table.
  useEffect(() => {
    if (!storeId || !cloverConnected) return;
    let cancelled = false;
    loadCloverCategoryNamesByPnl(storeId)
      .then((map) => { if (!cancelled) setCloverCatNamesByPnl(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [storeId, cloverConnected, stepKey === 'sales']);

  // ── Calculations & totals ────────────────────────────────────────
  // Actual COGS = Beginning + Purchases + Transfers In − Transfers Out
  //               ± Adjustments − Ending  (see utils/eopMath.js)
  const calculations = useMemo(() => {
    return pnlCategories.map((cat) => {
      const inv = inventoryValues[cat.id] || { beginning: 0, ending: 0 };
      const k = String(cat.id);
      const row = computeCategoryRow({
        beginning: inv.beginning,
        ending: inv.ending,
        purchases: purchases[cat.id],
        transfersIn: transfersInByCat[k],
        transfersOut: transfersOutByCat[k],
        adjustments: adjustmentsByCat[k],
        gross: grossSalesByCat[k],
        discounts: discountsByCat[k],
        comps: compsByCat[k],
        refunds: refundsByCat[k],
        waste: wasteByCat[k],
        salesUnavailable,
      });
      return {
        catId: cat.id,
        catName: cat.name,
        ...row,
        // legacy alias used by the legacy-edit save path and history views
        actualUsage: row.actualCOGS,
      };
    });
  }, [
    pnlCategories, inventoryValues, purchases,
    transfersInByCat, transfersOutByCat, adjustmentsByCat,
    grossSalesByCat, discountsByCat, compsByCat, refundsByCat, wasteByCat,
    salesUnavailable,
  ]);

  // Categories that actually participate in this period — they carry inventory
  // (from the selected counts), sales (from the pull/import), or a
  // manually-entered purchase/transfer/adjustment. Unused/unassigned categories
  // (e.g. App, Bills, Retail with $0 across the board) are hidden from the
  // per-category grids — Purchases & Adjustments, Sales by Category, and
  // Discounts/Comps/Waste — so the operator only edits the lines that matter and
  // all three steps show the same set. Totals (and the saved report) are summed
  // from the full set, so hiding zero rows never changes them.
  const isCategoryUsed = (c) => (
    c.beginning !== 0 || c.ending !== 0 || c.purchases !== 0 ||
    c.transfersIn !== 0 || c.transfersOut !== 0 || (c.adjustments || 0) !== 0 ||
    (c.grossSales || 0) !== 0 || (c.netSales || 0) !== 0 ||
    (c.discounts || 0) !== 0 || (c.comps || 0) !== 0 ||
    (c.refunds || 0) !== 0 || (c.waste || 0) !== 0
  );
  const usedCalculations = useMemo(() => {
    const used = calculations.filter(isCategoryUsed);
    // Guard: if nothing has data yet, fall back to the full list rather than
    // render an empty grid the user can't enter anything into.
    return used.length > 0 ? used : calculations;
  }, [calculations]);

  const totals = useMemo(() => {
    const t = {
      beginning: 0, ending: 0, purchases: 0, actualUsage: 0,
      transfersIn: 0, transfersOut: 0, adjustments: 0,
      grossSales: 0, hasGross: false,
      discounts: 0, comps: 0, refunds: 0, waste: 0,
      netSales: 0, hasSales: false,
      estDiscountCost: 0, estCompCost: 0, cogsPaidSales: 0,
      // Confirmed subtotals only — these list the categories whose missing
      // data keeps the corresponding total from being complete.
      netSalesIncompleteCats: [],
      discountsIncompleteCats: [],
      compsIncompleteCats: [],
      refundsIncompleteCats: [],
      allocationIncompleteCats: [],
    };
    calculations.forEach((c) => {
      t.beginning += c.beginning;
      t.ending += c.ending;
      t.purchases += c.purchases;
      t.actualUsage += c.actualUsage;
      t.transfersIn += c.transfersIn;
      t.transfersOut += c.transfersOut;
      t.adjustments += c.adjustments;
      t.discounts += c.discounts ?? 0;
      t.comps += c.comps ?? 0;
      t.refunds += c.refunds ?? 0;
      t.waste += c.waste ?? 0;
      t.estDiscountCost += c.estDiscountCost ?? 0;
      t.estCompCost += c.estCompCost ?? 0;
      if (c.grossSales != null) {
        t.hasGross = true;
        t.grossSales += c.grossSales;
      }
      if (c.netSales != null) {
        t.hasSales = true;
        t.netSales += c.netSales;
      }
      const inSalesScope = c.grossSales != null;
      if (inSalesScope && c.netSales == null) t.netSalesIncompleteCats.push(c.catName);
      if (inSalesScope && c.discounts == null) t.discountsIncompleteCats.push(c.catName);
      if (inSalesScope && c.comps == null) t.compsIncompleteCats.push(c.catName);
      if (inSalesScope && c.refunds == null) t.refundsIncompleteCats.push(c.catName);
      if (c.cogsPaidSales != null) {
        t.cogsPaidSales += c.cogsPaidSales;
      } else if (inSalesScope) {
        t.allocationIncompleteCats.push(c.catName);
      }
    });
    // Total percentages divide totals — never an average of category rates.
    t.impliedCostRate = t.hasGross ? computeImpliedCostRate(t.actualUsage, t.grossSales) : null;
    t.cogsPct = t.hasSales ? computeCogsPct(t.actualUsage, t.netSales) : null;
    t.adjustedCostPct = (t.hasSales && t.allocationIncompleteCats.length === 0 && t.netSalesIncompleteCats.length === 0)
      ? computeAdjustedCostPct(t.cogsPaidSales, t.netSales)
      : null;
    return t;
  }, [calculations]);

  // ── Report completeness & data notes ───────────────────────────────
  // Blank optional fields are treated as $0.00, so a report is complete
  // unless sales were explicitly marked unavailable for the period.
  const completeness = useMemo(() => {
    if (editingLegacyReport) return null;
    if (salesUnavailable) {
      return {
        level: 'incomplete_sales',
        label: 'Incomplete sales analysis — sales data was not available for this period.',
        salesMissing: ['Sales: marked unavailable for this period'],
      };
    }
    return { level: 'complete', label: 'Complete', salesMissing: [] };
  }, [salesUnavailable, editingLegacyReport]);

  // Report messaging (§ explanatory copy). A blank field is treated as
  // $0.00, so the only note needed is which fields were left blank.
  const dataNotes = useMemo(() => {
    if (editingLegacyReport || salesUnavailable) return [];
    const notes = [];
    const inScope = calculations.filter((c) => c.grossSales != null);
    [
      { field: 'discounts', noun: 'Discount' },
      { field: 'comps', noun: 'Comp' },
      { field: 'refunds', noun: 'Refund' },
    ].forEach(({ field, noun }) => {
      const blankCats = inScope
        .filter((c) => c[`${field}Status`] === DATA_STATUS.CONFIRMED_ZERO)
        .map((c) => c.catName);
      if (blankCats.length > 0) {
        notes.push(`No ${field} were reported for this period${blankCats.length === inScope.length ? '' : ` (${blankCats.join(', ')})`}. ${noun}-related calculations use $0.00.`);
      }
    });
    return notes;
  }, [calculations, salesUnavailable, editingLegacyReport]);

  // Shared wiring for the three sales adjustments — used by both the Sales by
  // Category step and the Discounts, Comps & Waste step.
  const salesAdjustmentFields = [
    ['discounts', discountsByCat, setDiscountsByCat],
    ['comps', compsByCat, setCompsByCat],
    ['refunds', refundsByCat, setRefundsByCat],
  ];

  // Categories whose gross sales are locked to an import (Clover or POS
  // report) — these drive the "Edit imported amounts" unlock above the
  // Sales by Category table.
  const importedSalesCatIds = useMemo(() => (
    pnlCategories
      .map((cat) => String(cat.id))
      .filter((k) => (grossSalesSourceByCat[k] === 'clover' && cloverSalesData?.category_sales?.[k] != null)
        || (grossSalesSourceByCat[k] === 'pos' && posApplied?.grossByCat?.[k] != null))
  ), [pnlCategories, grossSalesSourceByCat, cloverSalesData, posApplied]);
  const allImportedUnlocked = importedSalesCatIds.length > 0
    && importedSalesCatIds.every((k) => overrideUnlockedByCat[k]);

  // ── Apply an uploaded POS report to the wizard fields ───────────────
  // Writes gross sales per mapped category (locked like a Clover import),
  // any per-category deductions the report broke out, and report-level
  // deduction totals into Unallocated Adjustments. Values written by a
  // previous apply are cleared first (unless the user has since edited
  // them), so re-mapping never leaves stale figures behind.
  const applyPosReport = () => {
    if (!posExtraction) return;
    const plan = buildPosApplyPlan(posExtraction, posMapping);
    const prevApply = posApplied;

    const grossByCat = {};
    const deductionsByCat = {};
    Object.entries(plan.byCat).forEach(([k, t]) => {
      if (t.gross != null) grossByCat[k] = t.gross;
      const ded = {};
      ['discounts', 'comps', 'refunds'].forEach((f) => { if (t[f] != null) ded[f] = t[f]; });
      if (Object.keys(ded).length > 0) deductionsByCat[k] = ded;
    });

    setGrossSalesByCat((prev) => {
      const next = { ...prev };
      Object.keys(prevApply?.grossByCat || {}).forEach((k) => {
        if (grossByCat[k] == null && grossSalesSourceByCat[k] === 'pos') delete next[k];
      });
      Object.entries(grossByCat).forEach(([k, v]) => { next[k] = toMoneyString(v); });
      return next;
    });
    setGrossSalesSourceByCat((prev) => {
      const next = { ...prev };
      Object.keys(prevApply?.grossByCat || {}).forEach((k) => {
        if (grossByCat[k] == null && next[k] === 'pos') delete next[k];
      });
      Object.keys(grossByCat).forEach((k) => { next[k] = 'pos'; });
      return next;
    });
    setOverrideUnlockedByCat((prev) => {
      const next = { ...prev };
      Object.keys(grossByCat).forEach((k) => { delete next[k]; });
      return next;
    });

    // Per-category deductions the report broke out. A $0 period total needs
    // no per-field write — blank fields already count as $0.00 (the
    // zeroConfirmed list still records that the report stated "none").
    const deductionWiring = [
      ['discounts', setDiscountsByCat],
      ['comps', setCompsByCat],
      ['refunds', setRefundsByCat],
    ];
    deductionWiring.forEach(([field, setValues]) => {
      setValues((prev) => {
        const next = { ...prev };
        const prevCats = new Set([
          ...Object.keys(prevApply?.deductionsByCat || {}),
          ...Object.keys(prevApply?.proportionalByCat?.[field] || {}),
        ]);
        prevCats.forEach((k) => {
          // What the previous apply wrote: the report's per-category amount
          // plus any proportional split of the unallocated total.
          const prevAmt = prevApply.deductionsByCat?.[k]?.[field] ?? null;
          const prevShare = prevApply.proportionalByCat?.[field]?.[k] ?? null;
          if (prevAmt == null && prevShare == null) return;
          const written = round2((prevAmt ?? 0) + (prevShare ?? 0));
          if (deductionsByCat[k]?.[field] == null
            && parseOptionalAmount(next[k]) === written) delete next[k];
        });
        Object.entries(deductionsByCat).forEach(([k, ded]) => {
          if (ded[field] != null) next[k] = toMoneyString(ded[field]);
        });
        return next;
      });
    });

    // Deduction totals the report couldn't split by category land in the
    // Unallocated Adjustments row (allocatable later on the Discounts step).
    setUnallocated((prev) => {
      const next = { ...prev };
      ['discounts', 'comps', 'refunds'].forEach((f) => {
        const v = plan.unallocated[f];
        if (v != null && v > 0) next[f] = toMoneyString(v);
        else if (prevApply?.unallocated?.[f] != null && parseNum(prev[f]) === prevApply.unallocated[f]) next[f] = '';
      });
      return next;
    });

    const writtenUnallocated = {};
    ['discounts', 'comps', 'refunds'].forEach((f) => {
      if (plan.unallocated[f] != null && plan.unallocated[f] > 0) writtenUnallocated[f] = plan.unallocated[f];
    });

    setPosApplied({
      fileName: posFileName,
      posSystem: posExtraction.posSystem,
      periodStart: posExtraction.periodStart,
      periodEnd: posExtraction.periodEnd,
      basis: posExtraction.categorySalesBasis,
      appliedAt: new Date().toISOString(),
      grossByCat,
      deductionsByCat,
      unallocated: writtenUnallocated,
      allocationChoice: null, // 'manual' | 'proportional' once the user decides
      proportionalByCat: {}, // { [field]: { [catId]: share } } written by a split
      zeroConfirmed: ['discounts', 'comps', 'refunds'].filter((f) => posExtraction.totals[f] === 0),
      skipped: plan.skipped,
      totals: posExtraction.totals,
      warnings: posExtraction.warnings,
    });
    // The report gave deduction totals it couldn't split by category — make
    // the user pick a resolution (manual entry vs. proportional split) so the
    // amounts never sit as an open question.
    setPosAllocationPrompt(Object.keys(writtenUnallocated).length > 0
      ? { fields: writtenUnallocated }
      : null);
    const n = Object.keys(grossByCat).length;
    showToast(n > 0
      ? `POS report applied — gross sales filled for ${n} categor${n === 1 ? 'y' : 'ies'}.`
      : 'POS report applied.');
  };

  // Whether a proportional split has gross sales to base its shares on.
  const posSalesSharesAvailable = pnlCategories
    .some((c) => (parseOptionalAmount(grossSalesByCat[String(c.id)]) ?? 0) > 0);
  const hasUnallocatedDeductions = ['discounts', 'comps', 'refunds']
    .some((f) => (parseNum(unallocated[f]) || 0) > 0);

  // "Divide it up for me": split each unallocated deduction total across
  // categories using their share of gross sales, add the shares to the
  // per-category fields, and empty the Unallocated row. Used by the
  // post-apply dialog and the Discounts, Comps & Waste step.
  const splitUnallocatedBySales = () => {
    const salesByCat = {};
    pnlCategories.forEach((c) => {
      const v = parseOptionalAmount(grossSalesByCat[String(c.id)]);
      if (v != null && v > 0) salesByCat[String(c.id)] = v;
    });
    const splitByField = {};
    salesAdjustmentFields.forEach(([field, , setValues]) => {
      const total = parseNum(unallocated[field]);
      const shares = total != null && total > 0 ? allocateProportionally(total, salesByCat) : null;
      if (!shares) return;
      splitByField[field] = shares;
      setValues((prev) => {
        const next = { ...prev };
        Object.entries(shares).forEach(([k, share]) => {
          next[k] = toMoneyString((parseOptionalAmount(next[k]) ?? 0) + share);
        });
        return next;
      });
    });
    setPosAllocationPrompt(null);
    if (Object.keys(splitByField).length === 0) return;
    setUnallocated((prev) => {
      const next = { ...prev };
      Object.keys(splitByField).forEach((f) => { next[f] = ''; });
      return next;
    });
    setPosApplied((prev) => (prev ? {
      ...prev,
      allocationChoice: 'proportional',
      proportionalByCat: { ...(prev.proportionalByCat || {}), ...splitByField },
    } : prev));
    showToast(`Split ${Object.keys(splitByField).join(', ')} across categories in proportion to sales.`);
  };

  // "I'll allocate them manually": leave the totals in Unallocated
  // Adjustments for per-category entry on the Discounts, Comps & Waste step.
  const keepUnallocatedForManualEntry = () => {
    setPosApplied((prev) => (prev ? { ...prev, allocationChoice: 'manual' } : prev));
    setPosAllocationPrompt(null);
  };

  // Prior-period COGS% per category for the Review step. Uses the most recent
  // saved report whose date precedes this one (excluding the report being
  // edited).
  const priorPeriod = useMemo(() => {
    const refDate = endSession
      ? new Date(countDateFromSession(endSession) || 0).toISOString().slice(0, 10)
      : null;
    if (!refDate) return null;
    const prior = eopHistory
      .filter((r) => r.id !== savedReportId && r.id !== editingReportId)
      .filter((r) => (r.report_date || '') < refDate)
      .sort((a, b) => (b.report_date || '').localeCompare(a.report_date || ''))[0];
    if (!prior) return null;
    const byCat = {};
    (Array.isArray(prior.report_data) ? prior.report_data : []).forEach((r) => {
      // Only net-sales-based percentages are comparable across periods; a
      // gross-basis fallback (incomplete sales data) is excluded.
      if (isCategoryRow(r) && r.cogs_percent != null
        && (r.cogs_percent_basis == null || r.cogs_percent_basis === 'net')) {
        byCat[String(r.pnl_category_id)] = Number(r.cogs_percent);
      }
    });
    return { label: prior.period_label || `EOP ${formatDate(prior.report_date)}`, byCat };
  }, [eopHistory, endSession?.id, savedReportId, editingReportId]);

  // ── Step list & navigation ────────────────────────────────────────
  // Category Mapping appears only while required; legacy reports keep the
  // original short flow (their sales fields are preserved, not edited).
  const stepDefs = useMemo(() => {
    const defs = [{ key: 'period', label: 'Reporting Period' }];
    defs.push({ key: 'counts', label: 'Select Inventory Counts' });
    // Category assignment is scoped to the categories actually present in the
    // selected counts, so it follows the count picker rather than preceding it.
    if (mappingRequired) defs.push({ key: 'mapping', label: 'Assign Categories' });
    if (!editingLegacyReport) defs.push({ key: 'pos', label: 'Import POS Report' });
    defs.push({ key: 'purchases', label: editingLegacyReport ? 'Purchases' : 'Purchases & Adjustments' });
    if (!editingLegacyReport) {
      defs.push({ key: 'sales', label: 'Sales by Category' });
      defs.push({ key: 'adjustments', label: 'Discounts, Comps & Waste' });
    }
    defs.push({ key: 'review', label: 'Review & Generate' });
    return defs;
  }, [mappingRequired, editingLegacyReport]);

  const goToStepKey = (key, { markCurrentDone = true } = {}) => {
    if (markCurrentDone) {
      setCompletedKeys((prev) => {
        const next = new Set(prev);
        next.add(stepKey);
        return next;
      });
    }
    setStepKey(key);
  };

  // Mapping completeness — checked before the report starts (leaving the
  // period step) and re-checked against the selected counts' snapshot
  // categories when leaving the picker. If everything is mapped or excluded
  // the report is never interrupted.
  const runMappingCheck = async (extraCats = []) => {
    const status = await checkPnlMappingStatus(storeId, {
      cloverConnected,
      // Clover selling categories are classified later, in the sales-import
      // step — this step maps Countertop inventory categories only, and must
      // not be required or gated by Clover.
      includeClover: false,
      extraInventoryCategories: extraCats,
    });
    setPnlCategories(status.pnlCategories);
    setHasCategories(status.pnlCategories.length > 0);
    setMappingUnmapped({ inventory: status.unmappedInventory, clover: status.unmappedClover });
    if (status.required) setMappingRequired(true);
    return status;
  };

  const advanceFromPeriod = async () => {
    if (!storeId) return;
    setMappingCheckLoading(true);
    setError('');
    try {
      // Load P&L categories / mapping status for later steps, but always go to
      // the count picker first. The assignment step (if needed) is presented
      // afterward — scoped to the categories found in the selected counts —
      // rather than up front against the full catalog. Routing to mapping
      // before counts also caused a counts↔mapping loop.
      await runMappingCheck(sessionExtraCategories);
      goToStepKey('counts');
    } catch (_e) {
      // Never block reporting on a failed status check.
      goToStepKey('counts');
    } finally {
      setMappingCheckLoading(false);
    }
  };

  // The POS-report import sits between the counts picker and Purchases; the
  // mapping completeness checks already ran on the way in.
  const advanceFromPosImport = () => goToStepKey('purchases');

  const mappingResolved = mappingUnmapped.inventory.length === 0 && mappingUnmapped.clover.length === 0;

  const advanceFromMapping = async () => {
    if (!mappingResolved) return;
    try { await loadPnlCategories(storeId); } catch (_e) {}
    // Counts are selected before this step now, so continue forward into the
    // POS import (or straight to purchases for legacy reports) instead of
    // returning to the picker — that return path was the counts↔mapping loop.
    goToStepKey(editingLegacyReport ? 'purchases' : 'pos');
  };

  const advanceFromCounts = async ({ datesConfirmed = false } = {}) => {
    if (selectedSessionIds.length !== 2) {
      setError('Pick a beginning and an ending count to compare.');
      return;
    }
    setError('');
    if (!storeId) return;

    // Date sanity: confirm once when the ending count precedes the beginning
    // count and/or the two counts sit suspiciously close together. The dates
    // being compared are spelled out so it's clear what tripped the check
    // (count names can carry unrelated scheduled-date suffixes).
    if (!datesConfirmed) {
      const startISO = businessDateISOFromSession(beginSession);
      const endISO = businessDateISOFromSession(endSession);
      const diffDays = daysBetweenISO(startISO, endISO);
      const startLabel = formatDate(isoToLocalDate(startISO));
      const endLabel = formatDate(isoToLocalDate(endISO));
      const issues = [];
      if (diffDays != null && diffDays < 0) {
        issues.push(`your ending inventory (${endLabel}) occurred before your starting inventory (${startLabel})`);
      }
      if (diffDays != null && Math.abs(diffDays) < 7) {
        issues.push(`the inventory dates are only ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} apart (${startLabel} → ${endLabel})`);
      }
      if (issues.length > 0) {
        setCountsConfirm({
          body: `The inventories you have chosen: ${issues.join(', and ')}. Confirm that you've made the correct selection.`,
          onConfirm: () => {
            setCountsConfirm(null);
            advanceFromCounts({ datesConfirmed: true });
          },
        });
        return;
      }
    }

    setMappingCheckLoading(true);
    try {
      // Snapshot categories in the selected counts can include categories
      // that no longer exist on current inventory items — re-check with them.
      // Only concrete unmapped categories bounce back to the mapping step
      // here; the "Clover categories not imported yet" case was already
      // offered on entry, and bouncing on it again would loop for merchants
      // whose Clover catalog is empty.
      const distinctCats = await fetchDistinctCategoriesForSessions(selectedSessionIds);
      setSessionExtraCategories(distinctCats);
      const status = await runMappingCheck(distinctCats);
      if (status.unmappedInventory.length > 0 || status.unmappedClover.length > 0 || status.pnlCategories.length === 0) {
        goToStepKey('mapping');
        return;
      }
      goToStepKey(editingLegacyReport ? 'purchases' : 'pos');
    } catch (e) {
      setError(e.message || 'Failed to verify P&L mappings.');
    } finally {
      setMappingCheckLoading(false);
    }
  };

  // A manual inventory adjustment requires a note explaining it.
  const adjustmentsMissingNotes = useMemo(() => (
    pnlCategories.filter((cat) => {
      const k = String(cat.id);
      const adj = parseOptionalAmount(adjustmentsByCat[k]);
      return adj != null && adj !== 0 && !(adjustmentNotesByCat[k] || '').trim();
    }).map((cat) => cat.name)
  ), [pnlCategories, adjustmentsByCat, adjustmentNotesByCat]);

  const advanceFromPurchases = () => {
    if (!editingLegacyReport && adjustmentsMissingNotes.length > 0) {
      setError(`Add a note explaining the manual inventory adjustment for: ${adjustmentsMissingNotes.join(', ')}.`);
      return;
    }
    setError('');
    goToStepKey(editingLegacyReport ? 'review' : 'sales');
  };

  // Auto-import sales when the Sales by Category step opens for a
  // Clover-connected store (once per selection; re-sync stays manual).
  const autoPulledRef = useRef(false);
  useEffect(() => {
    autoPulledRef.current = false;
  }, [beginSession?.id, endSession?.id]);
  useEffect(() => {
    if (stepKey !== 'sales') return;
    if (!cloverConnected || editingLegacyReport) return;
    if (salesMode === 'manual' || salesUnavailable) return;
    if (cloverSalesData || cloverPulling) return;
    if (!cloverPullStart || !cloverPullEnd) return;
    if (autoPulledRef.current) return;
    autoPulledRef.current = true;
    pullCloverSales();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, cloverConnected, salesMode, salesUnavailable, cloverSalesData, cloverPulling, cloverPullStart, cloverPullEnd]);

  const handlePNLWizardComplete = async () => {
    setShowPNLWizard(false);
    if (!storeId) return;
    try {
      await runMappingCheck(sessionExtraCategories);
    } catch (_e) {}
  };

  // ── Save ────────────────────────────────────────────────────────
  const handleSave = async ({ missingConfirmed = false } = {}) => {
    if (!storeId || !beginSession || !endSession) {
      setError('Pick a beginning and ending count first.');
      return;
    }
    // When sales were marked unavailable, confirm before generating: the
    // user can still produce an inventory-usage report, but sales-dependent
    // calculations will be marked unavailable.
    if (!editingLegacyReport && !missingConfirmed && completeness) {
      const items = [...completeness.salesMissing];
      if (items.length > 0) {
        setMissingDataConfirm({ items });
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const reportDate = (() => {
        const d = countDateFromSession(endSession);
        return d ? new Date(d).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
      })();

      const finalReportName = (reportName || '').trim() || computeReportName(inventoryPeriodConfig, endSession) || null;

      // Preserve legacy sales fields when editing a legacy report.
      const legacyById = new Map();
      if (editingLegacyReport && Array.isArray(editingLegacyReport.report_data)) {
        editingLegacyReport.report_data.forEach((r) => {
          if (r && r.pnl_category_id != null) legacyById.set(r.pnl_category_id, r);
        });
      }

      const parsedNetSales = parseNum(netSalesTotal);
      const isLegacy = !!editingLegacyReport;

      let reportData;
      if (isLegacy) {
        // Legacy reports: original behavior — inventory & purchases are
        // editable, every saved sales field is preserved untouched.
        reportData = calculations.map((c) => {
          const legacy = legacyById.get(c.catId) || {};
          const catIdStr = String(c.catId);
          const catNetSalesParsed = parseNum(categorySalesOverrides[catIdStr]);
          const catNetSales = catNetSalesParsed != null ? catNetSalesParsed : (legacy.net_sales ?? null);
          const salesSrc = categorySalesSource[catIdStr] || null;
          const cogsPct = catNetSales && catNetSales > 0
            ? Math.round((c.actualUsage / catNetSales) * 10000) / 100
            : (legacy.cogs_percent ?? null);
          return {
            pnl_category_id: c.catId,
            pnl_category_name: c.catName,
            beginning_inventory: c.beginning,
            ending_inventory: c.ending,
            purchases: c.purchases,
            cost_of_goods_used: c.actualUsage,
            sales: legacy.sales ?? null,
            discounts: legacy.discounts ?? null,
            comps: legacy.comps ?? null,
            waste: legacy.waste ?? null,
            other_deductions: legacy.other_deductions ?? null,
            recipe_cost_pct: legacy.recipe_cost_pct ?? null,
            net_sales: catNetSales,
            cogs_percent: cogsPct,
            theoretical_usage: legacy.theoretical_usage ?? null,
            variance_dollars: legacy.variance_dollars ?? null,
            _sales_source: salesSrc,
          };
        });
      } else {
        // Category-level workflow rows (v3 — period_actual_cost_allocation_v1).
        // `cost_of_goods_used` is the Actual COGS from the full inventory
        // equation; waste is recorded separately and never subtracted from it.
        // Both the source values (with their data statuses) and the calculated
        // allocation values are persisted, so a historical report doesn't
        // change if counts, purchases, sales imports, or mappings are edited
        // later.
        reportData = calculations.map((c) => {
          const k = String(c.catId);
          const cloverGross = cloverSalesData?.category_sales?.[k];
          const posGross = posApplied?.grossByCat?.[k];
          const overridden = grossSalesSourceByCat[k] === 'manual' && (cloverGross != null || posGross != null);
          return {
            _v: 3,
            _calc_method: CALC_METHOD_ID,
            pnl_category_id: c.catId,
            pnl_category_name: c.catName,
            beginning_inventory: c.beginning,
            ending_inventory: c.ending,
            purchases: c.purchases,
            transfers_in: c.transfersIn,
            transfers_out: c.transfersOut,
            transfers_in_status: c.transfersInStatus,
            transfers_out_status: c.transfersOutStatus,
            other_adjustments: c.adjustments,
            adjustment_note: (adjustmentNotesByCat[k] || '').trim() || null,
            cost_of_goods_used: c.actualUsage,
            gross_sales: c.grossSales,
            discounts: c.discounts,
            comps: c.comps,
            refunds: c.refunds,
            discounts_status: c.discountsStatus,
            comps_status: c.compsStatus,
            refunds_status: c.refundsStatus,
            waste: c.waste,
            waste_status: c.wasteStatus,
            net_sales: c.netSales,
            cogs_percent: c.cogsPct,
            cogs_percent_basis: c.cogsPctBasis,
            implied_cost_rate: c.impliedCostRate,
            est_discount_cost: c.estDiscountCost,
            est_comp_cost: c.estCompCost,
            cogs_paid_sales: c.cogsPaidSales,
            adjusted_cost_pct: c.adjustedCostPct,
            // Refund cost is never auto-estimated until a POS integration
            // supplies item-level refund disposition data.
            refund_cost_estimated: false,
            clover_gross_sales: cloverGross ?? null,
            pos_report_gross_sales: posGross ?? null,
            override_note: overridden ? ((overrideNoteByCat[k] || '').trim() || null) : null,
            _sales_source: c.grossSales == null ? null
              : (grossSalesSourceByCat[k] === 'clover'
                ? ((c.discounts || c.comps || c.refunds) ? 'mixed' : 'clover')
                : grossSalesSourceByCat[k] === 'pos'
                  ? 'pos_report'
                  : ((cloverGross != null || posGross != null) ? 'mixed' : 'manual')),
          };
        });
        const unallocEntries = Object.entries(unallocated)
          .map(([field, v]) => [field, parseNum(v)])
          .filter(([, v]) => v != null && v !== 0);
        if (unallocEntries.length > 0) {
          reportData.push({
            _v: 3,
            _row_type: 'unallocated',
            pnl_category_id: null,
            pnl_category_name: 'Unallocated Adjustments',
            ...Object.fromEntries(unallocEntries),
          });
        }
        // Report-level calculation snapshot (completeness + totals + method).
        reportData.push({
          _v: 3,
          _row_type: 'meta',
          pnl_category_id: null,
          pnl_category_name: 'Report Calculation Snapshot',
          calc_method: CALC_METHOD_ID,
          completeness_status: completeness?.level ?? null,
          completeness_label: completeness?.label ?? null,
          completeness_notes: completeness ? [...completeness.salesMissing] : [],
          // Audit snapshot of the applied POS-report import (file-based, no
          // API connection) — original extracted figures plus what was written.
          pos_import: posApplied ? {
            file_name: posApplied.fileName,
            pos_system: posApplied.posSystem,
            period_start: posApplied.periodStart,
            period_end: posApplied.periodEnd,
            category_sales_basis: posApplied.basis,
            applied_at: posApplied.appliedAt,
            gross_sales_by_category: posApplied.grossByCat,
            deductions_by_category: posApplied.deductionsByCat,
            unallocated: posApplied.unallocated,
            allocation_choice: posApplied.allocationChoice ?? null,
            proportional_by_category: posApplied.proportionalByCat || {},
            zero_confirmed: posApplied.zeroConfirmed,
            skipped_lines: posApplied.skipped,
            report_totals: posApplied.totals,
            warnings: posApplied.warnings,
          } : null,
          totals: {
            gross_sales: totals.hasGross ? totals.grossSales : null,
            net_sales: totals.hasSales ? totals.netSales : null,
            actual_cogs: totals.actualUsage,
            implied_cost_rate: totals.impliedCostRate,
            est_discount_cost: totals.estDiscountCost,
            est_comp_cost: totals.estCompCost,
            cogs_paid_sales: totals.allocationIncompleteCats.length === 0 ? totals.cogsPaidSales : null,
            actual_cogs_pct: totals.cogsPct,
            adjusted_cost_pct: totals.adjustedCostPct,
            net_sales_incomplete_categories: totals.netSalesIncompleteCats,
            allocation_incomplete_categories: totals.allocationIncompleteCats,
          },
        });
      }

      // Aggregate COGS % across all categories (usage / net sales).
      // When sales were explicitly marked unavailable, no net-sales total is
      // recorded even if a Clover reference figure was synced.
      const netSalesNum = isLegacy
        ? (editingLegacyReport.net_sales_total ?? null)
        : (totals.hasSales ? totals.netSales : (salesUnavailable ? null : (parsedNetSales ?? null)));
      const aggCogsPct = netSalesNum && netSalesNum > 0
        ? Math.round((totals.actualUsage / netSalesNum) * 10000) / 100
        : (isLegacy ? (editingLegacyReport.aggregate_cogs_pct ?? null) : null);

      // Build Clover audit snapshot for persistence (original imported values
      // plus every explicit override, per the audit policy).
      const cloverAudit = cloverSalesData
        ? {
          ...cloverSalesData,
          overrides: {
            net_sales_total: netSalesTotalSource === 'manual' ? parsedNetSales : null,
            category_sales: isLegacy
              ? Object.fromEntries(
                Object.entries(categorySalesSource)
                  .filter(([, src]) => src === 'manual')
                  .map(([id]) => [id, parseNum(categorySalesOverrides[id])]),
              )
              : Object.fromEntries(
                Object.entries(grossSalesSourceByCat)
                  .filter(([id, src]) => src === 'manual' && cloverSalesData?.category_sales?.[id] != null)
                  .map(([id]) => [id, parseNum(grossSalesByCat[id])]),
              ),
            notes: isLegacy ? undefined : Object.fromEntries(
              Object.entries(overrideNoteByCat).filter(([, n]) => (n || '').trim()),
            ),
          },
        }
        : null;

      const row = {
        store_id: storeId,
        end_session_id: Number(endSession.id),
        start_session_id: Number(beginSession.id),
        report_date: reportDate,
        report_data: reportData,
        period_label: finalReportName,
        report_type: isLegacy ? (editingLegacyReport.report_type || 'usage_and_sales') : (netSalesNum ? 'usage_and_sales' : 'usage_only'),
        count_group_name: endSession?.template_name_snapshot || (isLegacy ? (editingLegacyReport.count_group_name ?? null) : null),
        actual_usage_total: totals.actualUsage,
        theoretical_usage_total: isLegacy ? (editingLegacyReport.theoretical_usage_total ?? null) : null,
        variance_dollars_total: isLegacy ? (editingLegacyReport.variance_dollars_total ?? null) : null,
        variance_pct_total: isLegacy ? (editingLegacyReport.variance_pct_total ?? null) : null,
        aggregate_cogs_pct: aggCogsPct,
        net_sales_total: netSalesNum,
        clover_sales_audit: cloverAudit,
      };

      let savedId = savedReportId;
      if (savedReportId) {
        const { error: updErr } = await supabase
          .from('pnl_reports')
          .update({ ...row, updated_at: new Date().toISOString() })
          .eq('id', savedReportId)
          .eq('store_id', storeId);
        if (updErr) throw updErr;
      } else {
        // Before inserting, check whether a report already exists for this
        // store + ending session (unique index on (store_id, end_session_id)).
        // If one exists, update it in place so we never hit a 409 conflict.
        const { data: existing } = await supabase
          .from('pnl_reports')
          .select('id')
          .eq('store_id', storeId)
          .eq('end_session_id', Number(endSession.id))
          .maybeSingle();

        if (existing?.id) {
          const { error: updErr } = await supabase
            .from('pnl_reports')
            .update({ ...row, updated_at: new Date().toISOString() })
            .eq('id', existing.id)
            .eq('store_id', storeId);
          if (updErr) throw updErr;
          savedId = existing.id;
          setSavedReportId(existing.id);
          setEditingReportId(existing.id);
        } else {
          const { data: inserted, error: insErr } = await supabase
            .from('pnl_reports')
            .insert([row])
            .select('id')
            .single();

          if (insErr) {
            // Race condition: another save landed between our lookup and the insert.
            // Retry as an update against the now-existing row.
            if (insErr.code === '23505') {
              const { data: conflicting } = await supabase
                .from('pnl_reports')
                .select('id')
                .eq('store_id', storeId)
                .eq('end_session_id', Number(endSession.id))
                .maybeSingle();
              if (conflicting?.id) {
                const { error: updErr } = await supabase
                  .from('pnl_reports')
                  .update({ ...row, updated_at: new Date().toISOString() })
                  .eq('id', conflicting.id)
                  .eq('store_id', storeId);
                if (updErr) throw updErr;
                savedId = conflicting.id;
                setSavedReportId(conflicting.id);
                setEditingReportId(conflicting.id);
              } else {
                throw new Error('A report for this ending count already exists — open it from history to edit.');
              }
            } else {
              throw insErr;
            }
          } else {
            savedId = inserted.id;
            setSavedReportId(inserted.id);
          }
        }
      }

      const hist = await loadEopHistory(storeId);
      const savedRow = (hist || []).find((r) => r.id === savedId);
      if (savedRow) {
        // Open the generated report directly rather than showing a post-save
        // options screen.
        setActiveHistoryRow(savedRow);
        setActiveTab('history');
      } else {
        // Couldn't resolve the saved row — fall back to the success summary.
        setSaveSuccess({ reportId: savedId, reportName: finalReportName || 'EOP Report' });
      }
    } catch (e) {
      setError(e.message || 'Failed to save report.');
    } finally {
      setSaving(false);
    }
  };

  const resetWizard = () => {
    setStepKey('period');
    setCompletedKeys(new Set());
    setPeriodEndChoice(null);
    setCustomPeriodEnd('');
    setPeriodChoiceTouched(false);
    setStartSessionId(null);
    setEndSessionId(null);
    setAutoSelectApplied(false);
    setCountsConfirm(null);
    setShowAllCounts(false);
    setDraggingRole(null);
    setPurchases({});
    setInventoryValues({});
    setReportName('');
    setReportNameEdited(false);
    setMappingRequired(false);
    setMappingUnmapped({ inventory: [], clover: [] });
    setSessionExtraCategories([]);
    setSavedReportId(null);
    setSaveSuccess(null);
    setEditingReportId(null);
    setEditingLegacyReport(null);
    setError('');
    // Category-level inputs
    setTransfersInByCat({});
    setTransfersOutByCat({});
    setAdjustmentsByCat({});
    setAdjustmentNotesByCat({});
    setGrossSalesByCat({});
    setGrossSalesSourceByCat({});
    setOverrideUnlockedByCat({});
    setOverrideNoteByCat({});
    setDiscountsByCat({});
    setCompsByCat({});
    setRefundsByCat({});
    setWasteByCat({});
    setMissingDataConfirm(null);
    setUnallocated({ discounts: '', comps: '', refunds: '', waste: '' });
    setSalesUnavailable(false);
    setSalesMode(null);
    setNegativeNetConfirmed(false);
    // Clover
    setCloverSalesData(null);
    setCloverError('');
    setCloverCategoryDiffs([]);
    setNetSalesTotal('');
    setNetSalesTotalSource(null);
    setCategorySalesOverrides({});
    setCategorySalesSource({});
    setCloverPullStart('');
    setCloverPullEnd('');
    // POS report import
    setPosExtraction(null);
    setPosFileName('');
    setPosMapping({});
    setPosApplied(null);
    setPosAllocationPrompt(null);
  };

  // ── Edit handling ───────────────────────────────────────────────
  const handleEditRequest = (report) => {
    setPendingEditReport(report);
    setEditConfirmStep(1);
  };
  const handleEditConfirmStep1 = () => setEditConfirmStep(2);
  const handleEditConfirmStep2 = () => {
    setEditConfirmStep(0);
    openEditReport(pendingEditReport);
    setPendingEditReport(null);
  };
  const handleEditCancel = () => {
    setEditConfirmStep(0);
    setPendingEditReport(null);
  };

  const openEditReport = (report) => {
    const legacy = isLegacyReport(report);
    setEditingReportId(report.id);
    setEditingLegacyReport(legacy ? report : null);
    setSavedReportId(report.id);
    setSaveSuccess(null);
    setError('');

    // Hydrate session selection from saved report
    setStartSessionId(report.start_session_id != null ? String(report.start_session_id) : null);
    setEndSessionId(report.end_session_id != null ? String(report.end_session_id) : null);
    // Saved-report selections are intentional — don't let auto-select override.
    setAutoSelectApplied(true);
    const ids = [];
    if (report.start_session_id != null) ids.push(String(report.start_session_id));
    if (report.end_session_id != null) ids.push(String(report.end_session_id));

    // Hydrate Clover audit from saved report
    if (report.clover_sales_audit) {
      setCloverSalesData(report.clover_sales_audit);
      if (report.net_sales_total != null && !legacy) {
        setNetSalesTotal(String(report.net_sales_total));
        setNetSalesTotalSource('clover');
      }
      // Restore per-category sales sources (legacy reports keep their saved
      // per-category NET sales in the legacy override state).
      const catSrc = {};
      const catOvr = {};
      (report.report_data || []).forEach((r) => {
        if (isCategoryRow(r) && r.net_sales != null && !(Number(r._v) >= 2)) {
          const src = r._sales_source || 'clover';
          catSrc[r.pnl_category_id] = src;
          catOvr[r.pnl_category_id] = String(r.net_sales);
        }
      });
      setCategorySalesSource(catSrc);
      setCategorySalesOverrides(catOvr);
    } else if (report.net_sales_total != null && !legacy) {
      setNetSalesTotal(String(report.net_sales_total));
      setNetSalesTotalSource('manual');
    } else {
      setNetSalesTotal('');
      setNetSalesTotalSource(null);
      setCloverSalesData(null);
      setCategorySalesOverrides({});
      setCategorySalesSource({});
    }

    // Hydrate purchases + category-level inputs from saved report_data
    const pur = {};
    const storedVals = {};
    const tIn = {}; const tOut = {}; const adj = {}; const adjNotes = {};
    const gross = {}; const grossSrc = {}; const ovrNotes = {};
    const disc = {}; const cmp = {}; const ref = {}; const wst = {};
    (report.report_data || []).forEach((r) => {
      if (!isCategoryRow(r)) return;
      const k = String(r.pnl_category_id);
      if (r.purchases != null) pur[r.pnl_category_id] = toMoneyString(r.purchases);
      storedVals[r.pnl_category_id] = {
        beginning: r.beginning_inventory || 0,
        ending: r.ending_inventory || 0,
      };
      // A saved nonzero amount hydrates the input. Rows the older workflow
      // saved with a non-reported data status have no amount and stay blank —
      // and a blank field is treated as zero.
      const hydrate = (value, valueMap) => {
        if (value) valueMap[k] = toMoneyString(value);
      };
      if (Number(r._v) >= 2) {
        hydrate(r.transfers_in, tIn);
        hydrate(r.transfers_out, tOut);
        if (r.other_adjustments) adj[k] = toMoneyString(r.other_adjustments);
        if (r.adjustment_note) adjNotes[k] = r.adjustment_note;
        if (r.gross_sales != null) gross[k] = toMoneyString(r.gross_sales);
        if (r._sales_source === 'clover') grossSrc[k] = 'clover';
        else if (r._sales_source === 'pos_report') grossSrc[k] = 'pos';
        else if (r.gross_sales != null) grossSrc[k] = 'manual';
        if (r.override_note) ovrNotes[k] = r.override_note;
        hydrate(r.discounts, disc);
        hydrate(r.comps, cmp);
        hydrate(r.refunds, ref);
        hydrate(r.waste, wst);
      }
    });
    setPurchases(pur);
    setInventoryValues(storedVals);
    setTransfersInByCat(tIn);
    setTransfersOutByCat(tOut);
    setAdjustmentsByCat(adj);
    setAdjustmentNotesByCat(adjNotes);
    setGrossSalesByCat(gross);
    setGrossSalesSourceByCat(grossSrc);
    setOverrideNoteByCat(ovrNotes);
    setOverrideUnlockedByCat({});
    setDiscountsByCat(disc);
    setCompsByCat(cmp);
    setRefundsByCat(ref);
    setWasteByCat(wst);

    const unallocRow = findUnallocatedRow(report.report_data);
    setUnallocated({
      discounts: unallocRow?.discounts != null ? toMoneyString(unallocRow.discounts) : '',
      comps: unallocRow?.comps != null ? toMoneyString(unallocRow.comps) : '',
      refunds: unallocRow?.refunds != null ? toMoneyString(unallocRow.refunds) : '',
      waste: unallocRow?.waste != null ? toMoneyString(unallocRow.waste) : '',
    });

    // Hydrate the POS-report import snapshot from the meta row so imported
    // values keep their labels (and lock) while editing. The original file
    // isn't kept — re-mapping requires uploading the report again.
    const posImport = findMetaRow(report.report_data)?.pos_import || null;
    setPosApplied(posImport ? {
      fileName: posImport.file_name || 'POS report',
      posSystem: posImport.pos_system ?? null,
      periodStart: posImport.period_start ?? null,
      periodEnd: posImport.period_end ?? null,
      basis: posImport.category_sales_basis || 'unknown',
      appliedAt: posImport.applied_at || report.updated_at || report.created_at,
      grossByCat: posImport.gross_sales_by_category || {},
      deductionsByCat: posImport.deductions_by_category || {},
      unallocated: posImport.unallocated || {},
      allocationChoice: posImport.allocation_choice ?? null,
      proportionalByCat: posImport.proportional_by_category || {},
      zeroConfirmed: posImport.zero_confirmed || [],
      skipped: posImport.skipped_lines || [],
      totals: posImport.report_totals || {},
      warnings: posImport.warnings || [],
    } : null);
    setPosExtraction(null);
    setPosFileName(posImport?.file_name || '');
    setPosMapping({});
    setPosAllocationPrompt(null);
    // "Sales unavailable" was checked iff the report carries no sales at all —
    // a usage_only report with saved gross sales just had unconfirmed
    // deductions, so the checkbox must stay off.
    setSalesUnavailable(report.report_type === 'usage_only'
      && (report.report_data || []).some((r) => Number(r?._v) >= 2)
      && !(report.report_data || []).some((r) => isCategoryRow(r) && r.gross_sales != null));
    setSalesMode(null);
    setNegativeNetConfirmed(false);
    setMappingRequired(false);
    setMappingUnmapped({ inventory: [], clover: [] });

    // Hydrate report name
    setReportName(report.period_label || '');
    setReportNameEdited(!!report.period_label);

    // If both sessions are present, land on the purchases step; otherwise send
    // the user back to the picker to choose a beginning count (legacy rows may
    // lack one).
    if (ids.length === 2) {
      setStepKey('purchases');
      setCompletedKeys(new Set(['period', 'pos', 'counts']));
    } else {
      setStepKey('counts');
      setCompletedKeys(new Set(['period', 'pos']));
      // Open the full count list so the missing partner is easy to find.
      setShowAllCounts(true);
    }
    setActiveTab('wizard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── History helpers ─────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sumReportField = (row, field) => {
    const arr = Array.isArray(row?.report_data) ? row.report_data : [];
    return arr.reduce((s, r) => s + (Number.isFinite(Number(r?.[field])) ? Number(r[field]) : 0), 0);
  };

  const sortValueForRow = (row, key) => {
    switch (key) {
      case 'report_date':
      case 'created_at':
        return new Date(row[key] || 0).getTime();
      case 'beginning_total': return sumReportField(row, 'beginning_inventory');
      case 'ending_total': return sumReportField(row, 'ending_inventory');
      case 'purchases_total': return sumReportField(row, 'purchases');
      case 'actual_usage_total':
        return Number.isFinite(Number(row.actual_usage_total))
          ? Number(row.actual_usage_total)
          : sumReportField(row, 'cost_of_goods_used');
      default: {
        const v = row[key];
        if (typeof v === 'string') return v.toLowerCase();
        return v ?? -Infinity;
      }
    }
  };

  const sortedHistory = useMemo(() => {
    const { key, dir } = sortConfig;
    return [...eopHistory].sort((a, b) => {
      const av = sortValueForRow(a, key);
      const bv = sortValueForRow(b, key);
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [eopHistory, sortConfig]);

  // Relative back-navigation within the active step list.
  const currentStepIdx = stepDefs.findIndex((d) => d.key === stepKey);
  const prevStepKey = currentStepIdx > 0 ? stepDefs[currentStepIdx - 1].key : null;
  const goBack = () => { if (prevStepKey) goToStepKey(prevStepKey, { markCurrentDone: false }); };

  // ── Review & Generate step content (shared desktop/mobile) ──────────
  const renderReviewStep = () => {
    // Validations: warnings never block generation; only missing required
    // data (or an unconfirmed negative net sales) does.
    const negativeRows = calculations.filter((c) => c.netSales != null && c.netSales < 0);
    const cogsNoSales = (!salesUnavailable && !editingLegacyReport)
      ? calculations.filter((c) => Math.abs(c.actualCOGS) > 0.005 && (c.grossSales == null || c.grossSales === 0))
      : [];
    const salesNoCogs = !editingLegacyReport
      ? calculations.filter((c) => c.grossSales != null && c.grossSales > 0 && Math.abs(c.actualCOGS) <= 0.005)
      : [];
    const overriddenCats = !editingLegacyReport
      ? calculations.filter((c) => {
        const k = String(c.catId);
        return grossSalesSourceByCat[k] === 'manual' && cloverSalesData?.category_sales?.[k] != null;
      })
      : [];
    const unallocTotal = ['discounts', 'comps', 'refunds', 'waste']
      .reduce((s, f) => s + (parseNum(unallocated[f]) || 0), 0);
    const salesMissing = !editingLegacyReport && !salesUnavailable && !totals.hasGross && !totals.hasSales;

    const warnings = [];
    if (salesUnavailable) warnings.push('Sales were marked unavailable for this period — COGS % will not be calculated.');
    if (cogsNoSales.length > 0) warnings.push(`COGS but no sales recorded: ${cogsNoSales.map((c) => c.catName).join(', ')}.`);
    if (salesNoCogs.length > 0) warnings.push(`Sales but no COGS recorded: ${salesNoCogs.map((c) => c.catName).join(', ')}.`);
    if (overriddenCats.length > 0) warnings.push(`Imported amounts manually overridden: ${overriddenCats.map((c) => c.catName).join(', ')}.`);
    if (unallocTotal > 0) warnings.push(`${formatCurrency(unallocTotal)} of adjustments is unallocated — enter category amounts on the Discounts, Comps & Waste step, or split them in proportion to sales there.`);
    if (posApplied) {
      if ((posApplied.skipped || []).length > 0) {
        warnings.push(`POS report lines not imported: ${posApplied.skipped.map((s) => `${s.name} (${formatCurrency(s.amount)})`).join(', ')} — map them on the Import POS Report step if they belong in a category.`);
      }
      if (posApplied.periodEnd && periodEndDate) {
        const drift = Math.abs(daysBetweenISO(posApplied.periodEnd, periodEndDate) ?? 0);
        if (drift > 3) {
          warnings.push(`The POS report covers a period ending ${formatDate(posApplied.periodEnd)}, but this report closes ${formatDate(periodEndDate)} — confirm the right report was uploaded.`);
        }
      }
    }

    const blockers = [];
    if (!beginSession || !endSession) blockers.push('Select a beginning and an ending count.');
    if (mappingRequired && !mappingResolved) blockers.push('Complete the Category Mapping step.');
    if (salesMissing) blockers.push('Enter sales for at least one category, or mark sales as unavailable on the Sales by Category step.');
    if (!editingLegacyReport && adjustmentsMissingNotes.length > 0) blockers.push(`Add a note for the manual inventory adjustment on: ${adjustmentsMissingNotes.join(', ')}.`);
    const needsNegativeConfirm = negativeRows.length > 0 && !negativeNetConfirmed;
    const canGenerate = !saving && blockers.length === 0 && !needsNegativeConfirm;

    const showSalesColumns = !editingLegacyReport;
    const parsedNS = parseNum(netSalesTotal);
    const legacyCogsPct = parsedNS > 0 && totals.actualUsage > 0
      ? Math.round((totals.actualUsage / parsedNS) * 10000) / 100
      : null;

    const summaryCards = showSalesColumns
      ? [
        { label: 'Beginning Inventory', value: formatCurrency(totals.beginning) },
        { label: '+ Purchases', value: formatCurrency(totals.purchases) },
        { label: '± Transfers & Adjustments', value: formatCurrency(totals.transfersIn - totals.transfersOut + totals.adjustments) },
        { label: '− Ending Inventory', value: formatCurrency(totals.ending) },
        { label: '= Actual COGS', value: formatCurrency(totals.actualUsage), highlight: true },
        ...(totals.hasGross ? [{ label: 'Gross Sales', value: formatCurrency(totals.grossSales) }] : []),
        ...(totals.hasSales ? [{
          label: 'Net Sales',
          value: formatCurrency(totals.netSales),
          incomplete: totals.netSalesIncompleteCats,
        }] : []),
        ...(totals.impliedCostRate != null ? [{
          label: 'Implied Actual Cost Rate',
          value: `${totals.impliedCostRate}%`,
          tip: 'Actual COGS divided by Gross Sales before discounts, comps, and refunds.',
        }] : []),
        ...(totals.allocationIncompleteCats.length === 0 && totals.hasSales && totals.impliedCostRate != null ? [{
          label: 'COGS Attributed to Paid Sales',
          value: formatCurrency(totals.cogsPaidSales),
        }] : []),
        ...(totals.cogsPct != null ? [{
          label: 'Actual COGS %',
          value: `${totals.cogsPct}%`,
          highlight: true,
          incomplete: totals.netSalesIncompleteCats,
        }] : []),
        ...(totals.adjustedCostPct != null ? [{
          label: 'Adjusted Cost %',
          value: `${totals.adjustedCostPct}%`,
          highlight: true,
          tip: 'COGS attributed to paid sales divided by Net Sales.',
        }] : []),
        ...(totals.waste > 0 ? [{ label: 'Waste (separate)', value: formatCurrency(totals.waste) }] : []),
      ]
      : [
        { label: 'Beginning Inventory', value: formatCurrency(totals.beginning) },
        { label: '+ Purchases', value: formatCurrency(totals.purchases) },
        { label: '− Ending Inventory', value: formatCurrency(totals.ending) },
        { label: '= Actual COGS', value: formatCurrency(totals.actualUsage), highlight: true },
        ...(parsedNS > 0 ? [
          { label: 'Net Sales', value: formatCurrency(parsedNS), clover: netSalesTotalSource === 'clover' },
          ...(legacyCogsPct != null ? [{ label: 'COGS %', value: `${legacyCogsPct}%`, highlight: true }] : []),
        ] : []),
      ];

    return (
      <div className="card mb-3">
        <div className="card-body">
          <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
            Review &amp; Generate
          </h6>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ color: '#3D2518', fontSize: '0.85rem' }}>
              Report name
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder='e.g. "April 2026 EOP"'
              maxLength={120}
              value={reportName}
              onChange={(e) => { setReportName(e.target.value); setReportNameEdited(true); }}
              style={{ maxWidth: 360 }}
            />
          </div>

          <div style={{ background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#3D2518' }}>
              {(reportName || '').trim() || computeReportName(inventoryPeriodConfig, endSession) || 'EOP Report'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#8B7B73', marginTop: 4 }}>
              Period: {formatDate(countDateFromSession(beginSession))} → {formatDate(countDateFromSession(endSession))}
              {priorPeriod ? ` · Prior period: ${priorPeriod.label}` : ''}
            </div>
          </div>

          {/* Report completeness */}
          {completeness && showSalesColumns && (() => {
            const palette = {
              complete: { bg: '#f0faf4', border: '#7dc99a', color: '#1a5c34' },
              incomplete_sales: { bg: '#FFF8F0', border: '#E0A878', color: '#A0522D' },
            }[completeness.level] || { bg: '#FAF6F2', border: '#E8DDD0', color: '#5A4A42' };
            const details = [...completeness.salesMissing];
            return (
              <div style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.82rem', color: palette.color }}>
                <strong>Report status:</strong> {completeness.label}
                {details.length > 0 && (
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem', fontSize: '0.76rem' }}>
                    {details.map((d) => <li key={d}>{d}</li>)}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* What came from the uploaded POS report */}
          {posApplied && showSalesColumns && (() => {
            const grossCats = Object.keys(posApplied.grossByCat)
              .map((k) => pnlCategories.find((c) => String(c.id) === k)?.name || `Category ${k}`);
            const parts = [];
            if (grossCats.length > 0) parts.push(`gross sales for ${grossCats.join(', ')}`);
            const unallocParts = Object.entries(posApplied.unallocated)
              .map(([f, v]) => `${f} ${formatCurrency(v)} (unallocated)`);
            if (unallocParts.length > 0) parts.push(unallocParts.join(', '));
            if (posApplied.zeroConfirmed.length > 0) parts.push(`${posApplied.zeroConfirmed.join(', ')} reported as none for this period`);
            return (
              <div style={{ background: '#F0FAF4', border: '1px solid #B7DCC3', borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#1a5c34' }}>
                <strong>Filled from POS report:</strong>{' '}
                {posApplied.fileName}{posApplied.posSystem ? ` (${posApplied.posSystem})` : ''}
                {parts.length > 0 ? ` — ${parts.join('; ')}.` : '.'}{' '}
                Imported values are labeled on the Sales by Category step.
              </div>
            );
          })()}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
            {summaryCards.map((b) => (
              <div key={b.label} style={{
                background: b.highlight ? '#FAF0E6' : '#f9f6f3',
                border: `1px solid ${b.highlight ? '#D4876A' : '#E8DDD0'}`,
                borderRadius: 10, padding: '0.6rem 0.85rem', minWidth: 140,
              }}>
                <div style={{ fontSize: '0.68rem', color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {b.label}
                  {b.clover && <span style={{ fontSize: '0.6rem', background: '#DDE8FA', color: '#1a4a7a', borderRadius: 6, padding: '0 4px', fontWeight: 700 }}>Clover</span>}
                  {b.tip && <InfoTip text={b.tip} />}
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: b.highlight ? '#A0522D' : '#3D2518', marginTop: '0.2rem' }}>
                  {b.value}
                  {b.incomplete && b.incomplete.length > 0 && <IncompleteBadge categories={b.incomplete} />}
                </div>
              </div>
            ))}
          </div>

          {showSalesColumns ? (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '0.75rem' }}>
              <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.76rem', minWidth: 1760 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: 110 }}>P&amp;L Category</th>
                    <th className="text-end">Beginning Inventory</th>
                    <th className="text-end">Purchases</th>
                    <th className="text-end">Transfers In</th>
                    <th className="text-end">Transfers Out</th>
                    <th className="text-end">Other Adjustment</th>
                    <th className="text-end">Ending Inventory</th>
                    <th className="text-end" style={{ background: '#faf6f3' }}>Actual COGS</th>
                    <th className="text-end">Gross Sales</th>
                    <th className="text-end">Discounts at Sales Value</th>
                    <th className="text-end">Comps at Sales Value</th>
                    <th className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      Refunds at Sales Value
                      <InfoTip text="Refund cost impact: Not estimated. Refunds may or may not have consumed inventory, so no cost is allocated until item-level refund disposition data is available." />
                    </th>
                    <th className="text-end" style={{ background: '#faf6f3' }}>Net Sales</th>
                    <th className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      Implied Actual Cost Rate
                      <InfoTip text="Actual COGS divided by Gross Sales before discounts, comps, and refunds." />
                    </th>
                    <th className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      Estimated Cost of Discounts
                      <InfoTip text="The estimated inventory cost associated with discounts entered at sales value." />
                    </th>
                    <th className="text-end">Estimated Cost of Comps</th>
                    <th className="text-end">COGS Attributed to Paid Sales</th>
                    <th className="text-end" style={{ background: '#faf6f3' }}>Actual COGS %</th>
                    <th className="text-end" style={{ background: '#faf6f3', whiteSpace: 'nowrap' }}>
                      Adjusted Cost %
                      <InfoTip text="COGS attributed to paid sales divided by Net Sales." />
                    </th>
                    <th className="text-end">Prior COGS %</th>
                    <th className="text-end">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.map((c) => {
                    const k = String(c.catId);
                    const prior = priorPeriod?.byCat?.[k] ?? null;
                    const netBasis = c.cogsPctBasis === 'net';
                    const variance = (netBasis && c.cogsPct != null && prior != null)
                      ? Math.round((c.cogsPct - prior) * 100) / 100
                      : null;
                    const flagged = (Math.abs(c.actualCOGS) > 0.005 && (c.grossSales == null || c.grossSales === 0) && !salesUnavailable)
                      || (c.grossSales != null && c.grossSales > 0 && Math.abs(c.actualCOGS) <= 0.005);
                    const overridden = grossSalesSourceByCat[k] === 'manual' && cloverSalesData?.category_sales?.[k] != null;
                    const inSalesScope = !salesUnavailable && c.grossSales != null;
                    const amountOrDash = (amount) => (amount != null ? formatCurrency(amount) : '—');
                    const allocCell = (amount) => {
                      if (amount != null) return formatCurrency(amount);
                      if (!inSalesScope) return '—';
                      const reasonText = c.allocationUnavailableReason === 'no_gross_sales'
                        ? 'Cost allocation unavailable because gross sales were not entered for this category.'
                        : c.allocationUnavailableReason === 'invalid_cogs'
                          ? 'Cost allocation unavailable. Complete purchases and resolve invalid or negative COGS before estimating the cost of discounts and comps.'
                          : 'Unavailable — the discount or comp amount for this category is missing.';
                      return <span title={reasonText} style={{ color: '#A0522D', fontSize: '0.68rem', fontStyle: 'italic', cursor: 'help' }}>Unavailable</span>;
                    };
                    return (
                      <tr key={c.catId} style={flagged ? { background: '#FFFBE6' } : undefined}>
                        <td className="fw-semibold" style={{ color: '#3D2518' }}>
                          {c.catName}
                          {overridden && (
                            <span title="Imported amount manually overridden" style={{ fontSize: '0.62rem', marginLeft: 4, padding: '1px 5px', borderRadius: 6, background: '#FFF3CD', color: '#856404', fontWeight: 700 }}>
                              override
                            </span>
                          )}
                          {flagged && (
                            <span title="Flagged: COGS without sales, or sales without COGS" style={{ fontSize: '0.7rem', marginLeft: 4 }}>⚠️</span>
                          )}
                        </td>
                        <td className="text-end">{formatCurrency(c.beginning)}</td>
                        <td className="text-end">{formatCurrency(c.purchases)}</td>
                        <td className="text-end">{formatCurrency(c.transfersIn)}</td>
                        <td className="text-end">{formatCurrency(c.transfersOut)}</td>
                        <td className="text-end">{formatCurrency(c.adjustments)}</td>
                        <td className="text-end">{formatCurrency(c.ending)}</td>
                        <td className="text-end fw-bold" style={{ background: '#faf6f3' }}>{formatCurrency(c.actualCOGS)}</td>
                        <td className="text-end">{c.grossSales != null ? formatCurrency(c.grossSales) : '—'}</td>
                        <td className="text-end">{amountOrDash(c.discounts)}</td>
                        <td className="text-end">{amountOrDash(c.comps)}</td>
                        <td className="text-end" title="Cost impact: Not estimated">{amountOrDash(c.refunds)}</td>
                        <td className="text-end fw-bold" style={{ background: '#faf6f3', color: c.netSales != null && c.netSales < 0 ? '#C0392B' : '#3D2518' }}>
                          {amountOrDash(c.netSales)}
                        </td>
                        <td className="text-end">{c.impliedCostRate != null ? `${c.impliedCostRate}%` : (inSalesScope ? allocCell(null) : '—')}</td>
                        <td className="text-end">{allocCell(c.estDiscountCost)}</td>
                        <td className="text-end">{allocCell(c.estCompCost)}</td>
                        <td className="text-end fw-semibold">{allocCell(c.cogsPaidSales)}</td>
                        <td className="text-end fw-bold" style={{ background: '#faf6f3' }}>
                          {c.cogsPct != null
                            ? <>
                              {c.cogsPct}%
                              {!netBasis && (
                                <span title="Net sales is incomplete for this category, so this percentage divides Actual COGS by Gross Sales." style={{ fontSize: '0.62rem', color: '#856404', marginLeft: 3, cursor: 'help' }}>
                                  of gross
                                </span>
                              )}
                            </>
                            : '—'}
                        </td>
                        <td className="text-end fw-bold" style={{ background: '#faf6f3' }}>{c.adjustedCostPct != null ? `${c.adjustedCostPct}%` : '—'}</td>
                        <td className="text-end" style={{ color: '#8B7B73' }}>{prior != null ? `${prior}%` : '—'}</td>
                        <td className="text-end" style={{ color: variance == null ? '#A89890' : variance > 0 ? '#C0392B' : '#1a5c34' }}>
                          {variance != null ? `${variance > 0 ? '+' : ''}${variance} pp` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {unallocTotal > 0 && (
                    <tr style={{ background: '#FFFDF6' }}>
                      <td className="fw-semibold" style={{ color: '#856404' }}>
                        Unallocated Adjustments
                        {(parseNum(unallocated.waste) || 0) > 0 && (
                          <div style={{ fontSize: '0.66rem', fontWeight: 400, color: '#8B7B73' }}>
                            includes {formatCurrency(parseNum(unallocated.waste) || 0)} waste
                          </div>
                        )}
                      </td>
                      <td className="text-end" colSpan={8} style={{ color: '#A89890' }}>—</td>
                      <td className="text-end">{(parseNum(unallocated.discounts) || 0) > 0 ? formatCurrency(parseNum(unallocated.discounts) || 0) : '—'}</td>
                      <td className="text-end">{(parseNum(unallocated.comps) || 0) > 0 ? formatCurrency(parseNum(unallocated.comps) || 0) : '—'}</td>
                      <td className="text-end">{(parseNum(unallocated.refunds) || 0) > 0 ? formatCurrency(parseNum(unallocated.refunds) || 0) : '—'}</td>
                      <td className="text-end" colSpan={9} style={{ color: '#A89890' }}>—</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="table-secondary">
                  <tr className="fw-bold">
                    <td>Totals</td>
                    <td className="text-end">{formatCurrency(totals.beginning)}</td>
                    <td className="text-end">{formatCurrency(totals.purchases)}</td>
                    <td className="text-end">{formatCurrency(totals.transfersIn)}</td>
                    <td className="text-end">{formatCurrency(totals.transfersOut)}</td>
                    <td className="text-end">{formatCurrency(totals.adjustments)}</td>
                    <td className="text-end">{formatCurrency(totals.ending)}</td>
                    <td className="text-end" style={{ background: '#f3ede8' }}>{formatCurrency(totals.actualUsage)}</td>
                    <td className="text-end">{totals.hasGross ? formatCurrency(totals.grossSales) : '—'}</td>
                    <td className="text-end">
                      {formatCurrency(totals.discounts)}
                      {totals.discountsIncompleteCats.length > 0 && <IncompleteBadge categories={totals.discountsIncompleteCats} />}
                    </td>
                    <td className="text-end">
                      {formatCurrency(totals.comps)}
                      {totals.compsIncompleteCats.length > 0 && <IncompleteBadge categories={totals.compsIncompleteCats} />}
                    </td>
                    <td className="text-end">
                      {formatCurrency(totals.refunds)}
                      {totals.refundsIncompleteCats.length > 0 && <IncompleteBadge categories={totals.refundsIncompleteCats} />}
                    </td>
                    <td className="text-end" style={{ background: '#f3ede8' }}>
                      {totals.hasSales ? formatCurrency(totals.netSales) : '—'}
                      {totals.netSalesIncompleteCats.length > 0 && <IncompleteBadge categories={totals.netSalesIncompleteCats} />}
                    </td>
                    <td className="text-end" title="Total Actual COGS ÷ Total Gross Sales — never an average of the category rates">
                      {totals.impliedCostRate != null ? `${totals.impliedCostRate}%` : '—'}
                    </td>
                    <td className="text-end">
                      {formatCurrency(totals.estDiscountCost)}
                      {totals.discountsIncompleteCats.length > 0 && <IncompleteBadge categories={totals.discountsIncompleteCats} />}
                    </td>
                    <td className="text-end">
                      {formatCurrency(totals.estCompCost)}
                      {totals.compsIncompleteCats.length > 0 && <IncompleteBadge categories={totals.compsIncompleteCats} />}
                    </td>
                    <td className="text-end">
                      {formatCurrency(totals.cogsPaidSales)}
                      {totals.allocationIncompleteCats.length > 0 && <IncompleteBadge categories={totals.allocationIncompleteCats} />}
                    </td>
                    <td className="text-end" style={{ background: '#f3ede8' }} title="Total Actual COGS ÷ Total Net Sales">
                      {totals.cogsPct != null ? `${totals.cogsPct}%` : '—'}
                      {totals.cogsPct != null && totals.netSalesIncompleteCats.length > 0 && <IncompleteBadge categories={totals.netSalesIncompleteCats} />}
                    </td>
                    <td className="text-end" style={{ background: '#f3ede8' }} title="Total COGS Attributed to Paid Sales ÷ Total Net Sales">
                      {totals.adjustedCostPct != null ? `${totals.adjustedCostPct}%` : '—'}
                    </td>
                    <td className="text-end" colSpan={2} style={{ color: '#A89890' }}>—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '0.75rem' }}>
              <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.82rem', minWidth: 480 }}>
                <thead className="table-light">
                  <tr>
                    <th>P&amp;L Category</th>
                    <th className="text-end">Beginning</th>
                    <th className="text-end">Purchases</th>
                    <th className="text-end">Ending</th>
                    <th className="text-end">Actual COGS</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.map((c) => (
                    <tr key={c.catId}>
                      <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                      <td className="text-end">{formatCurrency(c.beginning)}</td>
                      <td className="text-end">{formatCurrency(c.purchases)}</td>
                      <td className="text-end">{formatCurrency(c.ending)}</td>
                      <td className="text-end fw-bold">{formatCurrency(c.actualUsage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Usage allocation summary — Actual COGS split between paid sales
              and the estimated cost of discounts and comps. An allocation,
              never a change to Actual COGS. */}
          {showSalesColumns && !salesUnavailable && (() => {
            const allocatable = calculations.filter((c) => c.impliedCostRate != null);
            const unavailable = calculations.filter(
              (c) => c.impliedCostRate == null && (Math.abs(c.actualCOGS) > 0.005 || c.grossSales != null),
            );
            if (allocatable.length === 0 && unavailable.length === 0) return null;
            const allocRow = (label, amount, { negative = false, strong = false } = {}) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontWeight: strong ? 700 : 500 }}>{label}</span>
                <span style={{ fontWeight: strong ? 700 : 600, whiteSpace: 'nowrap' }}>
                  {amount == null ? 'Unavailable' : negative ? `(${formatCurrency(amount)})` : formatCurrency(amount)}
                </span>
              </div>
            );
            return (
              <div style={{ marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#3D2518', marginBottom: 6 }}>
                  Usage Allocation by Category
                  <InfoTip text="Allocates Actual COGS between paid sales and the estimated cost of discounts and comps. These allocations do not change Actual COGS." />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                  {allocatable.map((c) => (
                    <div key={c.catId} style={{ background: '#FFFDF9', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.6rem 0.85rem', minWidth: 250, fontSize: '0.76rem', color: '#5A4A42' }}>
                      <div style={{ fontWeight: 700, color: '#3D2518', marginBottom: 4 }}>{c.catName}</div>
                      {allocRow('Actual Inventory Usage', c.actualCOGS)}
                      {allocRow('Less: Estimated Cost of Discounts', c.estDiscountCost, { negative: c.estDiscountCost != null })}
                      {allocRow('Less: Estimated Cost of Comps', c.estCompCost, { negative: c.estCompCost != null })}
                      <div style={{ borderTop: '1px solid #E8DDD0', marginTop: 4, paddingTop: 4 }}>
                        {allocRow('COGS Attributed to Paid Sales', c.cogsPaidSales, { strong: true })}
                      </div>
                    </div>
                  ))}
                  {allocatable.length > 1 && totals.allocationIncompleteCats.length === 0 && (
                    <div style={{ background: '#FAF0E6', border: '1px solid #D4876A', borderRadius: 10, padding: '0.6rem 0.85rem', minWidth: 250, fontSize: '0.76rem', color: '#5A4A42' }}>
                      <div style={{ fontWeight: 700, color: '#A0522D', marginBottom: 4 }}>All Categories</div>
                      {allocRow('Actual Inventory Usage', totals.actualUsage)}
                      {allocRow('Less: Estimated Cost of Discounts', totals.estDiscountCost, { negative: true })}
                      {allocRow('Less: Estimated Cost of Comps', totals.estCompCost, { negative: true })}
                      <div style={{ borderTop: '1px solid #D4876A', marginTop: 4, paddingTop: 4 }}>
                        {allocRow('COGS Attributed to Paid Sales', totals.cogsPaidSales, { strong: true })}
                      </div>
                    </div>
                  )}
                </div>
                {unavailable.length > 0 && (
                  <div style={{ fontSize: '0.74rem', color: '#856404', marginTop: 6 }}>
                    {unavailable.map((c) => (
                      <div key={c.catId}>
                        <strong>{c.catName}:</strong>{' '}
                        {c.allocationUnavailableReason === 'no_gross_sales'
                          ? 'Cost allocation unavailable because gross sales were not entered for this category.'
                          : 'Cost allocation unavailable. Complete purchases and resolve invalid or negative COGS before estimating the cost of discounts and comps.'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Methodology + waste (analytical) */}
          {showSalesColumns && (
            <div style={{ fontSize: '0.76rem', color: '#5A4A42', background: '#FFFDF9', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: '0.85rem' }}>
              <div style={{ marginBottom: totals.waste > 0 ? 4 : 0 }}>
                Actual COGS is calculated from beginning inventory, purchases, transfers,
                adjustments, and ending inventory. Estimated discount and comp costs allocate a
                portion of Actual COGS using the category's implied cost rate, calculated as
                Actual COGS divided by Gross Sales. These allocations do not change Actual COGS.
                {totals.refunds > 0 && (
                  <> Refunds are shown at sales value; their cost impact is <strong>Not estimated</strong>.</>
                )}
              </div>
              {totals.waste > 0 && (
                <div>
                  COGS excluding separately recorded waste{' '}
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: '#856404', background: '#FFF3CD', borderRadius: 8, padding: '1px 7px' }}>analytical</span>:{' '}
                  <strong>{formatCurrency(totals.actualUsage - totals.waste)}</strong> — not the official COGS.
                </div>
              )}
            </div>
          )}

          {/* Data notes — which blank fields were treated as $0.00 */}
          {showSalesColumns && dataNotes.length > 0 && (
            <div style={{ fontSize: '0.76rem', color: '#5A4A42', background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: '0.85rem' }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Data notes</div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {dataNotes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="alert alert-warning py-2 small" style={{ marginBottom: '0.75rem' }}>
              <strong>Check before generating</strong> (warnings don't block the report):
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                {warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}

          {negativeRows.length > 0 && (
            <div className="alert alert-danger py-2 small" style={{ marginBottom: '0.75rem' }}>
              Net sales is <strong>negative</strong> for: {negativeRows.map((c) => c.catName).join(', ')}.
              <div className="form-check" style={{ marginTop: 4 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="eop-negative-confirm"
                  checked={negativeNetConfirmed}
                  onChange={(e) => setNegativeNetConfirmed(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="eop-negative-confirm">
                  I've checked these figures — generate the report anyway
                </label>
              </div>
            </div>
          )}

          {blockers.length > 0 && (
            <div className="alert alert-danger py-2 small" style={{ marginBottom: '0.75rem' }}>
              <strong>Required before generating:</strong>
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.2rem' }}>
                {blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
            <div style={{ display: 'flex', gap: 10 }}>
              {editingReportId && (
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetWizard}>
                  Cancel Edit
                </button>
              )}
              <button
                type="button"
                className="btn"
                style={{ background: !canGenerate ? '#c9bdb6' : '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.6rem 1.5rem', fontSize: '0.9rem', border: 'none', cursor: canGenerate ? 'pointer' : 'not-allowed' }}
                disabled={!canGenerate}
                onClick={() => handleSave()}
              >
                {saving ? (<><span className="spinner-border spinner-border-sm me-2" />Saving…</>) : (editingReportId ? 'Update Report' : 'Generate Report')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {showPNLWizard && (
        <PNLSetupWizard
          storeId={storeId}
          onClose={() => setShowPNLWizard(false)}
          onComplete={handlePNLWizardComplete}
        />
      )}

      {editConfirmStep === 1 && (
        <ConfirmDialog
          title="Edit this report?"
          body="You're about to edit a saved End of Period report. This will update the record and may affect the next period's beginning inventory values."
          onConfirm={handleEditConfirmStep1}
          onCancel={handleEditCancel}
          confirmLabel="Yes, I want to edit it"
          confirmVariant="warning"
        />
      )}
      {editConfirmStep === 2 && (
        <ConfirmDialog
          title="Are you sure?"
          body="This action cannot be undone from the app. Saving will overwrite the existing record and downstream beginning values will update accordingly."
          onConfirm={handleEditConfirmStep2}
          onCancel={handleEditCancel}
          confirmLabel="Open for editing"
          confirmVariant="danger"
        />
      )}

      {countsConfirm && (
        <ConfirmDialog
          title="Check your inventory selection"
          body={countsConfirm.body}
          onConfirm={countsConfirm.onConfirm}
          onCancel={() => setCountsConfirm(null)}
          confirmLabel="Yes, use these counts"
          confirmVariant="warning"
        />
      )}

      {posAllocationPrompt && (
        <PosAllocationDialog
          fields={posAllocationPrompt.fields}
          canSplit={posSalesSharesAvailable}
          onManual={keepUnallocatedForManualEntry}
          onSplit={splitUnallocatedBySales}
        />
      )}

      {missingDataConfirm && (
        <MissingDataDialog
          items={missingDataConfirm.items}
          onCancel={() => setMissingDataConfirm(null)}
          onConfirm={() => {
            setMissingDataConfirm(null);
            handleSave({ missingConfirmed: true });
          }}
        />
      )}

      {posLeaveConfirm && (
        <LeaveWithoutApplyingDialog
          onCancel={() => setPosLeaveConfirm(false)}
          onConfirm={() => { setPosLeaveConfirm(false); advanceFromPosImport(); }}
        />
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1.25rem', borderBottom: '2px solid #E8DDD0', alignItems: 'flex-end' }}>
        {[
          { id: 'wizard', label: editingReportId ? '✏️ Edit Report' : '📝 New Report' },
          { id: 'history', label: `📋 History${eopHistory.length ? ` (${eopHistory.length})` : ''}` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              if (tab.id === 'wizard' && activeTab !== 'wizard' && editingReportId) {
                // staying in wizard if already editing
              }
              if (tab.id !== 'wizard') {
                setEditingReportId(null);
                setEditingLegacyReport(null);
                setSaveSuccess(null);
              }
              setActiveTab(tab.id);
            }}
            style={{
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #5A4A42' : '2px solid transparent',
              marginBottom: -2,
              padding: '0.55rem 1.1rem',
              fontWeight: activeTab === tab.id ? 700 : 500,
              color: activeTab === tab.id ? '#3D2518' : '#8B7B73',
              cursor: 'pointer', fontSize: '0.9rem',
            }}
          >
            {tab.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {typeof onNavigateToSettings === 'function' && (
          <button
            type="button"
            onClick={onNavigateToSettings}
            style={{
              background: 'none', border: '1px solid #D4C4B0', borderRadius: 7,
              padding: '4px 12px', marginBottom: 6,
              color: '#5A4A42', cursor: 'pointer', fontSize: '0.78rem',
              fontFamily: "'Montserrat', sans-serif", fontWeight: 600,
            }}
            title="Review or change category mappings outside a report"
          >
            ⚙️ P&amp;L Category Mapping
          </button>
        )}
      </div>

      {/* ════════════════ WIZARD TAB ════════════════ */}
      {activeTab === 'wizard' && (
        <div>
          {hasCategories === false && stepKey === 'period' && (
            <div style={{ background: '#FFF8F0', border: '2px solid #E8DDD0', borderRadius: 14, padding: '1.5rem', marginBottom: '1.25rem', textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#3E2723', marginBottom: '0.35rem' }}>
                P&amp;L categories aren't set up yet
              </div>
              <p style={{ fontSize: '0.83rem', color: '#8B7B73', marginBottom: '1rem', lineHeight: 1.5 }}>
                You can start the report now; a required Category Mapping step will guide you through the assignments.
              </p>
              <button
                type="button"
                onClick={() => setShowPNLWizard(true)}
                style={{ background: '#5A4A42', color: '#FAF6F2', border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}
              >
                Set Up P&amp;L Categories
              </button>
              {storeId && hasInProgressWizard(storeId) && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.76rem', color: '#8B7B73' }}>
                  You have an unfinished setup.{' '}
                  <button type="button" onClick={() => setShowPNLWizard(true)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5A4A42', textDecoration: 'underline', padding: 0 }}>
                    Resume →
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="alert alert-danger alert-dismissible mb-3" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError('')} />
            </div>
          )}

          {editingReportId && editingLegacyReport && (
            <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.82rem', color: '#5A4A42' }}>
              <span style={{
                fontSize: '0.7rem', padding: '3px 9px', borderRadius: 8, fontWeight: 700,
                background: '#EAF2FB', color: '#1a4a7a', border: '1px solid #BFD4EE',
              }}>
                Legacy report
              </span>
              <span>Sales fields are hidden — inventory & purchases are editable; saved sales values are preserved.</span>
            </div>
          )}

          {editingReportId && !editingLegacyReport && (
            <div className="alert alert-warning d-flex align-items-center justify-content-between gap-2 mb-3">
              <span>
                <strong>Editing saved report</strong> — changes will overwrite the existing record when you save.
              </span>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetWizard}>Cancel Edit</button>
            </div>
          )}

          {saveSuccess && (() => {
            const successBody = (
              <div style={{ background: '#f0faf4', border: '1px solid #7dc99a', borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#1a5c34', fontSize: '1rem', marginBottom: '0.4rem' }}>
                  ✅ End of Period report saved: {saveSuccess.reportName}
                </div>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <button type="button" className="btn btn-sm" style={{ background: '#2c7a4b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600 }}
                    onClick={() => {
                      const row = eopHistory.find((r) => r.id === saveSuccess.reportId);
                      if (row) setActiveHistoryRow(row);
                      setActiveTab('history');
                      setSaveSuccess(null);
                    }}>
                    View Report
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 8 }}
                    onClick={() => { resetWizard(); if (typeof onBack === 'function') onBack(); }}>
                    Back to Reports
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 8 }}
                    onClick={resetWizard}>
                    Start Another
                  </button>
                </div>
              </div>
            );
            return isMobile ? (
              <MobileReportPopup
                title={mobileOutputTitle}
                onClose={() => { resetWizard(); if (typeof onBack === 'function') onBack(); }}
              >
                {successBody}
              </MobileReportPopup>
            ) : successBody;
          })()}

          {!saveSuccess && (
            <>
              <Stepper steps={stepDefs} activeKey={stepKey} completedKeys={completedKeys} onJump={(k) => goToStepKey(k, { markCurrentDone: false })} />

              {/* Selected reporting period — visible on every step after the first */}
              {stepKey !== 'period' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10,
                  padding: '0.45rem 0.85rem', marginBottom: '0.85rem', fontSize: '0.82rem', color: '#3D2518',
                }}>
                  <span>
                    <strong>Reporting period:</strong>{' '}
                    {beginSession && endSession
                      ? `${formatDate(countDateFromSession(beginSession))} → ${formatDate(countDateFromSession(endSession))}`
                      : (periodEndDate ? `Period ending ${formatDate(periodEndDate)}` : '—')}
                  </span>
                  {(reportName || '').trim() && (
                    <span style={{ color: '#8B7B73' }}>· {(reportName || '').trim()}</span>
                  )}
                </div>
              )}

              {/* ── Step: Reporting Period ── */}
              {stepKey === 'period' && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      Reporting Period
                    </h6>
                    <p style={{ fontSize: '0.82rem', color: '#8B7B73' }}>
                      Which period are you closing? We've pre-selected the period end nearest to
                      today{inventoryPeriodConfig?.enabled ? ' from your accounting calendar' : ''} — pick a
                      different one from the list, or enter a custom close date.
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div>
                        <label className="form-label small fw-semibold">Period closing</label>
                        <select
                          className="form-select form-select-sm"
                          value={periodEndChoice || ''}
                          onChange={(e) => {
                            setPeriodChoiceTouched(true);
                            setPeriodEndChoice(e.target.value || null);
                          }}
                          style={{ minWidth: 230 }}
                          aria-label="Period being closed"
                        >
                          <option value="custom">Custom date…</option>
                          {periodOptions.map((opt) => (
                            <option key={opt.iso} value={opt.iso}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      {periodEndChoice === 'custom' && (
                        <div>
                          <label className="form-label small fw-semibold">Period close date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={customPeriodEnd}
                            onChange={(e) => { setPeriodChoiceTouched(true); setCustomPeriodEnd(e.target.value); }}
                            style={{ width: 170 }}
                            aria-label="Custom period close date"
                          />
                        </div>
                      )}
                    </div>
                    {periodEndDate && (
                      <div style={{ marginTop: '0.7rem', fontSize: '0.8rem', color: '#5A4A42', background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.5rem 0.85rem' }}>
                        Closing the period ending <strong>{formatDate(periodEndDate)}</strong>
                        {prevPeriodEndDate && (
                          <> — the previous period closed <strong>{formatDate(prevPeriodEndDate)}</strong>, so we'll
                          look for completed counts near both dates.</>
                        )}
                      </div>
                    )}

                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        style={{ padding: 0, color: '#7A6259', fontSize: '0.78rem' }}
                        onClick={() => setShowScheduleEditor((v) => !v)}
                      >
                        {showScheduleEditor ? 'Hide period schedule' : 'Edit period schedule'}
                      </button>
                      {showScheduleEditor && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <InventoryScheduleEditor
                            storeId={storeId}
                            onSaved={(newCfg) => setInventoryPeriodConfig(newCfg)}
                          />
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        onClick={advanceFromPeriod}
                        disabled={!periodEndDate || mappingCheckLoading}
                      >
                        {mappingCheckLoading ? 'Checking setup…' : 'Next →'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Category Mapping (only when required) ── */}
              {stepKey === 'mapping' && (
                <div className="card mb-3">
                  <div className="card-body">
                    {/* The panel renders the "Assign Inventory Categories to P&L
                        Categories" title, the inventory-vs-P&L explanation, and
                        the row-based assignment table. Clover selling categories
                        are intentionally excluded here — sold Clover items are
                        classified into P&L categories later, in the sales-import
                        step — so this step maps Countertop inventory only. */}
                    <PNLCategoryMappingPanel
                      storeId={storeId}
                      includeClover={false}
                      extraInventoryCategories={sessionExtraCategories}
                      onStatusChange={({ unmappedInventory, unmappedClover }) => {
                        setMappingUnmapped({ inventory: unmappedInventory, clover: unmappedClover });
                      }}
                    />

                    {!mappingResolved && (
                      <div className="alert alert-warning py-2 small mt-3 mb-0">
                        {mappingUnmapped.inventory.length > 0 && (
                          <div>
                            <strong>{mappingUnmapped.inventory.length}</strong> Countertop categor{mappingUnmapped.inventory.length === 1 ? 'y' : 'ies'} still need{mappingUnmapped.inventory.length === 1 ? 's' : ''} a P&amp;L assignment: {mappingUnmapped.inventory.slice(0, 5).join(', ')}{mappingUnmapped.inventory.length > 5 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ marginTop: '0.75rem', fontSize: '0.76rem', color: '#8B7B73' }}>
                      You can review or change these assignments anytime in{' '}
                      <strong>Settings → P&amp;L Category Mapping</strong>.
                    </div>

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none', opacity: mappingResolved ? 1 : 0.55 }}
                        disabled={!mappingResolved}
                        onClick={advanceFromMapping}
                        title={mappingResolved ? undefined : 'Assign or exclude every category to continue'}
                      >
                        Continue →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Select Inventory Counts ── */}
              {stepKey === 'counts' && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      Select Inventory Counts
                    </h6>
                    <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '0.75rem' }}>
                      Based on your period close of <strong>{formatDate(periodEndDate)}</strong>
                      {prevPeriodEndDate ? <> (previous close <strong>{formatDate(prevPeriodEndDate)}</strong>)</> : null},
                      we've selected the completed counts nearest those dates — a count finished just
                      after midnight still lines up with its close day. Use{' '}
                      <strong>Choose Other</strong> to pick any count from your history instead.
                    </p>

                    {loadingSessions ? (
                      <div className="text-muted small">Loading sessions…</div>
                    ) : eligibleSessions.length === 0 ? (
                      <div className="alert alert-info py-2 small mb-0">
                        No completed counts yet. Complete an end-of-period count first.
                      </div>
                    ) : (
                      <>
                        {/* Labeled selection summary: Beginning Inventory first,
                            Ending Inventory second, with the count's name,
                            completion date & time, item count, and counter. */}
                        {(() => {
                          const summaryRow = (label, sess, accent) => {
                            if (!sess) {
                              return (
                                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                  <span style={{ minWidth: 150, textAlign: 'left', fontWeight: 700, color: accent, fontSize: '0.82rem' }}>{label}</span>
                                  <span style={{ fontSize: '0.8rem', color: '#A89890', fontStyle: 'italic' }}>
                                    Not selected — use Choose Other below
                                  </span>
                                </div>
                              );
                            }
                            const when = countDateFromSession(sess);
                            return (
                              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <span style={{
                                  minWidth: 150, textAlign: 'left', fontWeight: 700,
                                  color: accent, fontSize: '0.82rem',
                                }}>
                                  {label}
                                </span>
                                <span style={{ fontSize: '0.84rem', color: '#3D2518', fontWeight: 600 }}>
                                  {sess.name || `Session ${sess.id}`}
                                </span>
                                <span style={{ fontSize: '0.78rem', color: '#8B7B73' }}>
                                  {formatDate(when)}
                                  {formatTime12h(when) ? ` · ${formatTime12h(when)}` : ''}
                                  {sess.items_counted ? ` · ${sess.items_counted} items` : ''}
                                  {sess.created_by && userMap[sess.created_by] ? ` · by ${userMap[sess.created_by]}` : ''}
                                </span>
                              </div>
                            );
                          };
                          return (
                            <div style={{ padding: '0.7rem 0.9rem', background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {summaryRow('Beginning Inventory', beginSession, '#246B45')}
                              {summaryRow('Ending Inventory', endSession, '#285F8F')}
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  style={{ fontSize: '0.78rem' }}
                                  onClick={handleSwap}
                                  disabled={!startSessionId || !endSessionId}
                                >
                                  ⇆ Swap inventory assignments
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  style={{ fontSize: '0.78rem' }}
                                  onClick={() => setShowAllCounts((v) => !v)}
                                >
                                  {showAllCounts ? 'Hide full list' : '📋 Choose Other'}
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {showAllCounts && (
                          <>
                            <div style={{ fontSize: '0.76rem', color: '#8B7B73', margin: '0.75rem 0 0.4rem' }}>
                              All completed counts — assigned counts first, then newest first. Assign any
                              count as the beginning or ending inventory (drag a label onto a different
                              count, or use the buttons).
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
                              {displayCounts.map((s) => {
                                const idStr = String(s.id);
                                const isStart = String(startSessionId || '') === idStr;
                                const isEnd = String(endSessionId || '') === idStr;
                                const role = isStart ? 'start' : isEnd ? 'end' : null;
                                const dropEligible = !!draggingRole;
                                // Beginning is muted green; ending muted blue (red
                                // stays reserved for errors/warnings).
                                const accent = role === 'start' ? '#32875A' : role === 'end' ? '#4A7FA8' : 'transparent';
                                const tagBg = role === 'start' ? '#E8F5EE' : '#EAF2FA';
                                const tagColor = role === 'start' ? '#246B45' : '#285F8F';
                                return (
                                  <div
                                    key={s.id}
                                    onDragOver={dropEligible ? (e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                    } : undefined}
                                    onDrop={dropEligible ? (e) => {
                                      e.preventDefault();
                                      const dropped = draggingRole || e.dataTransfer.getData('text/plain');
                                      setDraggingRole(null);
                                      if (dropped === 'start' || dropped === 'end') {
                                        requestAssignment(dropped, idStr);
                                      }
                                    } : undefined}
                                    style={{
                                      display: 'flex', alignItems: 'stretch',
                                      borderRadius: 10,
                                      border: '1px solid #E8DDD0',
                                      background: '#fff',
                                      overflow: 'hidden',
                                      outline: dropEligible && !role ? '1px dashed #B9A89A' : 'none',
                                      outlineOffset: 2,
                                      transition: 'border-color 120ms ease, background 120ms ease',
                                    }}
                                  >
                                    <div aria-hidden="true" style={{ width: 4, flexShrink: 0, background: accent }} />
                                    <div style={{ flex: 1, minWidth: 0, padding: '0.7rem 0.9rem' }}>
                                      {role && (
                                        <span
                                          draggable
                                          onDragStart={(e) => {
                                            setDraggingRole(role);
                                            e.dataTransfer.effectAllowed = 'move';
                                            e.dataTransfer.setData('text/plain', role);
                                          }}
                                          onDragEnd={() => setDraggingRole(null)}
                                          title={role === 'start'
                                            ? 'Beginning inventory — drag onto another count to reassign'
                                            : 'Ending inventory — drag onto another count to reassign'}
                                          style={{
                                            display: 'inline-block',
                                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 8, fontWeight: 700,
                                            background: tagBg, color: tagColor,
                                            cursor: 'grab', userSelect: 'none',
                                            marginBottom: 4,
                                          }}
                                        >
                                          {role === 'start' ? 'Beginning Inventory' : 'Ending Inventory'}
                                        </span>
                                      )}
                                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#3D2518' }}>
                                        {s.name || `Session ${s.id}`}
                                      </div>
                                      <div style={{ fontSize: '0.76rem', color: '#8B7B73', marginTop: 2 }}>
                                        {formatDate(countDateFromSession(s))}
                                        {formatTime12h(countDateFromSession(s)) ? ` · ${formatTime12h(countDateFromSession(s))}` : ''}
                                        {s.template_name_snapshot ? ` · template: ${s.template_name_snapshot}` : ''}
                                        {s.items_counted ? ` · ${s.items_counted} items` : ''}
                                        {s.created_by && userMap[s.created_by] ? ` · by ${userMap[s.created_by]}` : ''}
                                      </div>
                                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                        <button
                                          type="button"
                                          onClick={() => requestAssignment('start', idStr)}
                                          disabled={isStart}
                                          style={{
                                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 8,
                                            border: '1px solid #32875A', cursor: isStart ? 'default' : 'pointer',
                                            background: isStart ? '#E8F5EE' : '#fff', color: '#246B45',
                                            opacity: isStart ? 0.7 : 1,
                                          }}
                                        >
                                          {isStart ? '✓ Beginning' : 'Set as Beginning'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => requestAssignment('end', idStr)}
                                          disabled={isEnd}
                                          style={{
                                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 10px', borderRadius: 8,
                                            border: '1px solid #4A7FA8', cursor: isEnd ? 'default' : 'pointer',
                                            background: isEnd ? '#EAF2FA' : '#fff', color: '#285F8F',
                                            opacity: isEnd ? 0.7 : 1,
                                          }}
                                        >
                                          {isEnd ? '✓ Ending' : 'Set as Ending'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        disabled={selectedSessionIds.length !== 2 || mappingCheckLoading}
                        onClick={() => advanceFromCounts()}
                      >
                        {mappingCheckLoading ? 'Checking mappings…' : 'Next →'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Import POS Report (optional) ── */}
              {stepKey === 'pos' && !editingLegacyReport && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      Import POS Report
                      <span style={{ fontSize: '0.66rem', fontWeight: 600, color: '#8B7B73', background: '#F0EAE4', borderRadius: 8, padding: '1px 8px', marginLeft: 8, verticalAlign: 'middle' }}>
                        optional
                      </span>
                    </h6>
                    <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '0.75rem' }}>
                      {cloverConnected
                        ? <>Pull this period's sales straight from Clover, or upload the end-of-period report your POS exports — either way we match it to this report's fields (sales by category, discounts, comps, and refunds) and fill them in for you. </>
                        : <>Upload the end-of-period report your POS exports and we'll match it to this report's fields — sales by category, discounts, comps, and refunds — and fill them in for you. </>}
                      Every imported value is labeled with where it came from, and
                      anything the report doesn't cover stays editable on the later steps. Skip this
                      to enter sales manually.
                    </p>

                    <EOPPosReportImport
                      storeId={storeId}
                      pnlCategories={pnlCategories}
                      extraction={posExtraction}
                      fileName={posFileName}
                      mapping={posMapping}
                      applied={posApplied}
                      cloverConnected={cloverConnected}
                      cloverPeriodStart={cloverPullStart || proposedPullStart}
                      cloverPeriodEnd={cloverPullEnd || proposedPullEnd}
                      squareConnected={squareConnected}
                      squarePeriodStart={cloverPullStart || proposedPullStart}
                      squarePeriodEnd={cloverPullEnd || proposedPullEnd}
                      onExtracted={({ extraction, fileName, initialMapping }) => {
                        setPosExtraction(extraction);
                        setPosFileName(fileName);
                        // Seeded from the store's saved report-name mappings —
                        // past corrections beat the AI match on re-import.
                        setPosMapping(initialMapping || {});
                      }}
                      onMappingChange={setPosMapping}
                      onCategoryAdded={(cat) => {
                        setPnlCategories((prev) => [...prev, cat]);
                        setHasCategories(true);
                      }}
                      onApply={() => { applyPosReport(); advanceFromPosImport(); }}
                      onClear={() => { setPosExtraction(null); setPosFileName(''); setPosMapping({}); }}
                    />

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        onClick={() => {
                          // Leaving with an uploaded-but-unapplied report drops
                          // those sales — confirm first. Applied or no report:
                          // advance straight through.
                          if (posExtraction && !posApplied) setPosLeaveConfirm(true);
                          else advanceFromPosImport();
                        }}
                      >
                        {posApplied
                          ? 'Next →'
                          : posExtraction
                            ? 'Continue without applying →'
                            : 'Skip — enter sales manually →'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Purchases & Inventory Adjustments ── */}
              {stepKey === 'purchases' && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      {editingLegacyReport ? 'Enter purchases & review usage' : 'Purchases & Inventory Adjustments'}
                    </h6>

                    {loadingInventory ? (
                      <div className="text-muted small d-flex align-items-center gap-2 mb-3">
                        <span className="spinner-border spinner-border-sm" />
                        Loading inventory values…
                      </div>
                    ) : pnlCategories.length === 0 ? (
                      <div className="alert alert-info py-2 small">No P&L categories — set them up first.</div>
                    ) : editingLegacyReport ? (
                      <>
                        <p style={{ fontSize: '0.8rem', color: '#8B7B73', marginBottom: '0.6rem', fontStyle: 'italic' }}>
                          Actual COGS = Beginning Inventory + Purchases − Ending Inventory
                        </p>
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.84rem', minWidth: 480 }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ minWidth: 130 }}>P&amp;L Category</th>
                                <th className="text-end" style={{ minWidth: 110 }}>Beginning Inv.</th>
                                <th className="text-end" style={{ minWidth: 110 }}>Ending Inv.</th>
                                <th style={{ minWidth: 145 }}>Purchases ($)</th>
                                <th className="text-end" style={{ minWidth: 110, background: '#faf6f3' }}>Actual COGS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usedCalculations.map((c) => (
                                <tr key={c.catId}>
                                  <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                  <td className="text-end" style={{ color: '#5A4A42' }}>{formatCurrency(c.beginning)}</td>
                                  <td className="text-end" style={{ color: '#5A4A42' }}>{formatCurrency(c.ending)}</td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      placeholder="0.00"
                                      min="0"
                                      step="0.01"
                                      value={purchases[c.catId] || ''}
                                      onChange={(e) => setPurchases((prev) => ({ ...prev, [c.catId]: e.target.value }))}
                                      onBlur={(e) => {
                                        const settled = toMoneyString(e.target.value);
                                        if (settled !== e.target.value) setPurchases((prev) => ({ ...prev, [c.catId]: settled }));
                                      }}
                                    />
                                  </td>
                                  <td className="text-end fw-bold" style={{ background: '#faf6f3', color: c.actualUsage < 0 ? '#C0392B' : '#3D2518' }}>
                                    {formatCurrency(c.actualUsage)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td>Totals</td>
                                <td className="text-end">{formatCurrency(totals.beginning)}</td>
                                <td className="text-end">{formatCurrency(totals.ending)}</td>
                                <td className="text-end">{formatCurrency(totals.purchases)}</td>
                                <td className="text-end" style={{ background: '#f3ede8' }}>{formatCurrency(totals.actualUsage)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    ) : (
                      <>
                        <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '0.5rem' }}>
                          Beginning and ending inventory come from your selected counts. Enter
                          purchases for the period, plus any transfers or other inventory
                          adjustments.
                        </p>
                        <div style={{ background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.55rem 0.85rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#3D2518' }}>
                          <strong>Actual COGS</strong> = Beginning Inventory + Purchases + Transfers In − Transfers Out ± Other Adjustment − Ending Inventory
                          <div style={{ fontSize: '0.74rem', color: '#8B7B73', marginTop: 3 }}>
                            Other Adjustment accepts positive and negative amounts: a <strong>positive</strong> amount
                            adds cost to the period (e.g. found shrinkage), a <strong>negative</strong> amount removes
                            cost (e.g. a credited write-off). A note is required for every manual adjustment.
                          </div>
                        </div>
                        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.82rem', minWidth: 880 }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ minWidth: 120 }}>P&amp;L Category</th>
                                <th className="text-end" style={{ minWidth: 100 }}>Beginning Inventory</th>
                                <th style={{ minWidth: 120 }}>Purchases ($)</th>
                                <th style={{ minWidth: 110 }}>Transfers In ($)</th>
                                <th style={{ minWidth: 110 }}>Transfers Out ($)</th>
                                <th style={{ minWidth: 130 }}>Other Adjustment (±$)</th>
                                <th className="text-end" style={{ minWidth: 100 }}>Ending Inventory</th>
                                <th className="text-end" style={{ minWidth: 110, background: '#faf6f3' }}>Calculated COGS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usedCalculations.map((c) => {
                                const k = String(c.catId);
                                const adjVal = parseOptionalAmount(adjustmentsByCat[k]);
                                const needsNote = adjVal != null && adjVal !== 0;
                                const noteMissing = needsNote && !(adjustmentNotesByCat[k] || '').trim();
                                return (
                                  <React.Fragment key={c.catId}>
                                    <tr>
                                      <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                      <td className="text-end" style={{ color: '#5A4A42' }}>{formatCurrency(c.beginning)}</td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          placeholder="0.00"
                                          min="0"
                                          step="0.01"
                                          value={purchases[c.catId] || ''}
                                          onChange={(e) => setPurchases((prev) => ({ ...prev, [c.catId]: e.target.value }))}
                                          onBlur={(e) => {
                                            const settled = toMoneyString(e.target.value);
                                            if (settled !== e.target.value) setPurchases((prev) => ({ ...prev, [c.catId]: settled }));
                                          }}
                                          aria-label={`${c.catName} purchases`}
                                        />
                                      </td>
                                      <td>
                                        <AmountCell
                                          value={transfersInByCat[k]}
                                          onValueChange={(v) => setTransfersInByCat((prev) => ({ ...prev, [k]: v }))}
                                          ariaLabel={`${c.catName} transfers in`}
                                        />
                                      </td>
                                      <td>
                                        <AmountCell
                                          value={transfersOutByCat[k]}
                                          onValueChange={(v) => setTransfersOutByCat((prev) => ({ ...prev, [k]: v }))}
                                          ariaLabel={`${c.catName} transfers out`}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          placeholder="±0.00"
                                          step="0.01"
                                          value={adjustmentsByCat[k] || ''}
                                          onChange={(e) => setAdjustmentsByCat((prev) => ({ ...prev, [k]: e.target.value }))}
                                          onBlur={(e) => {
                                            const settled = toMoneyString(e.target.value);
                                            if (settled !== e.target.value) setAdjustmentsByCat((prev) => ({ ...prev, [k]: settled }));
                                          }}
                                          aria-label={`${c.catName} other adjustment`}
                                        />
                                      </td>
                                      <td className="text-end" style={{ color: '#5A4A42' }}>{formatCurrency(c.ending)}</td>
                                      <td className="text-end fw-bold" style={{ background: '#faf6f3', color: c.actualCOGS < 0 ? '#C0392B' : '#3D2518' }}>
                                        {formatCurrency(c.actualCOGS)}
                                      </td>
                                    </tr>
                                    {needsNote && (
                                      <tr>
                                        <td colSpan={8} style={{ background: '#FFFDF6', padding: '0.4rem 0.6rem' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '0.74rem', fontWeight: 700, color: noteMissing ? '#A0522D' : '#5A4A42', whiteSpace: 'nowrap' }}>
                                              Adjustment note{noteMissing ? ' (required)' : ''}:
                                            </span>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              placeholder={`Why was ${c.catName} adjusted by ${formatCurrency(adjVal)}?`}
                                              maxLength={240}
                                              value={adjustmentNotesByCat[k] || ''}
                                              onChange={(e) => setAdjustmentNotesByCat((prev) => ({ ...prev, [k]: e.target.value }))}
                                              style={{ flex: '1 1 260px', borderColor: noteMissing ? '#E0A878' : undefined }}
                                              aria-label={`${c.catName} adjustment note`}
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td>Totals</td>
                                <td className="text-end">{formatCurrency(totals.beginning)}</td>
                                <td className="text-end">{formatCurrency(totals.purchases)}</td>
                                <td className="text-end">{formatCurrency(totals.transfersIn)}</td>
                                <td className="text-end">{formatCurrency(totals.transfersOut)}</td>
                                <td className="text-end">{formatCurrency(totals.adjustments)}</td>
                                <td className="text-end">{formatCurrency(totals.ending)}</td>
                                <td className="text-end" style={{ background: '#f3ede8' }}>{formatCurrency(totals.actualUsage)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <p style={{ fontSize: '0.74rem', color: '#8B7B73', marginTop: '0.5rem', marginBottom: 0 }}>
                          Purchases will pre-fill automatically once purchase imports are connected;
                          until then, enter the period's purchase totals per category.
                        </p>
                      </>
                    )}

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        onClick={advanceFromPurchases}
                        disabled={loadingInventory || pnlCategories.length === 0}
                      >
                        {editingLegacyReport ? 'Review →' : 'Next →'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Sales by Category ── */}
              {stepKey === 'sales' && !editingLegacyReport && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      Sales by Category
                    </h6>
                    <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '0.75rem' }}>
                      {cloverConnected
                        ? 'Sales import from Clover for the selected period and are grouped by your saved category mappings — no need to pick categories again. Imported amounts are locked; use “Edit imported amounts” above the table to change them.'
                        : posApplied
                          ? 'Sales were filled from your uploaded POS report and are labeled “POS Report” below. Imported amounts are locked; use “Edit imported amounts” above the table to change them — everything the report didn’t cover stays editable here.'
                          : 'Enter gross sales for the period by P&L category. Discounts, comps, and refunds are entered on the next step; net sales is calculated automatically.'}
                    </p>

                    {/* Top-level source selector — only shown when more than one
                        source is available (i.e. Clover is connected). */}
                    {cloverConnected && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
                        {[
                          { id: 'clover', label: '🍀 Import from Clover' },
                          { id: 'clover-adjust', label: 'Import from Clover and adjust' },
                          { id: 'manual', label: 'Enter manually' },
                        ].map((opt) => {
                          const active = (salesMode || 'clover') === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setSalesMode(opt.id);
                                if (opt.id === 'clover-adjust') {
                                  const all = {};
                                  pnlCategories.forEach((cat) => { all[String(cat.id)] = true; });
                                  setOverrideUnlockedByCat(all);
                                }
                              }}
                              style={{
                                border: `1.5px solid ${active ? '#1a4a7a' : '#D4C4B0'}`,
                                background: active ? '#EAF2FB' : '#fff',
                                color: active ? '#1a4a7a' : '#5A4A42',
                                borderRadius: 8, padding: '5px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Clover import panel ── */}
                    {cloverConnected && (salesMode || 'clover') !== 'manual' && (
                      <div style={{
                        marginBottom: '1rem',
                        background: '#F0F7FF',
                        border: '1.5px solid #BFD4EE',
                        borderRadius: 12,
                        padding: '1rem 1.25rem',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: '0.65rem' }}>
                          <div style={{ fontWeight: 700, color: '#1a4a7a', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🍀</span>
                            <span>Clover Sales</span>
                            {cloverSalesData && (
                              <span style={{ fontSize: '0.7rem', background: '#1a4a7a', color: '#fff', borderRadius: 8, padding: '1px 8px', fontWeight: 600 }}>
                                {cloverSalesData.order_count} orders
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: cloverSalesData ? '#E8F1FD' : '#1a4a7a',
                              color: cloverSalesData ? '#1a4a7a' : '#fff',
                              border: '1px solid #BFD4EE',
                              borderRadius: 8,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              minWidth: 160,
                            }}
                            onClick={pullCloverSales}
                            disabled={cloverPulling || !cloverPullStart || !cloverPullEnd}
                            title={`Sync sales from ${cloverPullStart || '…'} to ${cloverPullEnd || '…'}`}
                          >
                            {cloverPulling
                              ? <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
                              : cloverSalesData
                              ? '🔄 Re-sync from Clover'
                              : '🍀 Sync from Clover'}
                          </button>
                        </div>

                        {/* ── Sales period (derived from the selected inventory counts) ── */}
                        <div style={{ background: '#fff', border: '1px solid #D7E3F4', borderRadius: 10, padding: '0.7rem 0.85rem', marginBottom: '0.75rem', fontSize: '0.78rem', color: '#1a4a7a' }}>
                          <strong>Clover sales period:</strong> {cloverPullStart || '…'} → {cloverPullEnd || '…'}
                          <div style={{ fontSize: '0.72rem', color: '#5A6A7A', marginTop: 4 }}>
                            Set automatically from the selected inventory counts; sales start the day after the starting count and end on the ending count date.
                          </div>
                        </div>

                        {cloverError && (
                          <div style={{ fontSize: '0.8rem', color: '#7A1F18', background: '#FBEAEA', borderRadius: 7, padding: '0.4rem 0.75rem', marginBottom: '0.6rem' }}>
                            {cloverError}
                          </div>
                        )}

                        {cloverSalesData && (
                          <div style={{ fontSize: '0.8rem', color: '#3D2518', marginBottom: '0.65rem' }}>
                            <span style={{ color: '#8B7B73' }}>Last synced:</span>{' '}
                            {new Date(cloverSalesData.pulled_at).toLocaleString()}
                            {' · '}
                            <span style={{ color: '#8B7B73' }}>Period:</span>{' '}
                            {cloverSalesData.period_start} → {cloverSalesData.period_end}
                          </div>
                        )}

                        {cloverSalesData && (
                          <div style={{ fontSize: '0.8rem', color: '#3D2518', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.68rem', padding: '1px 7px', borderRadius: 8, fontWeight: 700, background: '#DDE8FA', color: '#1a4a7a', border: '1px solid #BFD4EE', flexShrink: 0 }}>
                              From Clover
                            </span>
                            <span>
                              Net sales reported by Clover for this window:{' '}
                              <strong>{formatCurrency(Number(cloverSalesData.net_sales_total) || 0)}</strong>{' '}
                              <span style={{ color: '#8B7B73' }}>(order totals less tax — reference)</span>
                            </span>
                          </div>
                        )}

                        {cloverCategoryDiffs.length > 0 && (
                          <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', background: '#FFFBE6', borderRadius: 7, border: '1px solid #FFE69C', fontSize: '0.78rem', color: '#664D03' }}>
                            <strong>Changes since last pull:</strong>
                            <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem' }}>
                              {cloverCategoryDiffs.map(({ catId, prev, next }) => {
                                const cat = pnlCategories.find((c) => String(c.id) === catId);
                                const name = cat?.name ?? `Category ${catId}`;
                                const sign = next > prev ? '+' : '';
                                return (
                                  <li key={catId}>
                                    <strong>{name}</strong>: {formatCurrency(prev)} → {formatCurrency(next)}{' '}
                                    <span style={{ color: next >= prev ? '#155724' : '#721c24' }}>({sign}{formatCurrency(next - prev)})</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {!cloverSalesData && !cloverPulling && (
                          <div style={{ fontSize: '0.78rem', color: '#5A6A7A', marginTop: '0.5rem' }}>
                            Sales import automatically for {cloverPullStart || '…'} – {cloverPullEnd || '…'}; use the sync button if nothing loads.
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── POS report applied banner ── */}
                    {posApplied && (
                      <div style={{
                        marginBottom: '1rem', background: '#F0FAF4', border: '1.5px solid #B7DCC3',
                        borderRadius: 12, padding: '0.7rem 1rem', fontSize: '0.8rem', color: '#1a5c34',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.68rem', padding: '1px 7px', borderRadius: 8, fontWeight: 700, background: '#E0F0E5', color: '#1a5c34', border: '1px solid #B7DCC3', flexShrink: 0 }}>
                            From POS report
                          </span>
                          <span>
                            <strong>{posApplied.fileName}</strong>
                            {posApplied.posSystem ? ` (${posApplied.posSystem})` : ''}
                            {(posApplied.periodStart || posApplied.periodEnd)
                              ? ` · ${posApplied.periodStart || '…'} → ${posApplied.periodEnd || '…'}`
                              : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link"
                          style={{ fontSize: '0.74rem', padding: '0 4px', color: '#1a5c34' }}
                          onClick={() => goToStepKey('pos', { markCurrentDone: false })}
                        >
                          Edit import
                        </button>
                      </div>
                    )}

                    {/* ── Sales by Category table ── */}
                    {/* One unlock for the whole table — imported amounts stay
                        locked until the user opts into editing them, then every
                        imported field opens at once. */}
                    {!salesUnavailable && importedSalesCatIds.length > 0 && !(cloverConnected && salesMode === 'manual') && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginBottom: '0.45rem', flexWrap: 'wrap' }}>
                        {allImportedUnlocked && (
                          <span style={{ fontSize: '0.74rem', color: '#8B7B73' }}>
                            Imported amounts are unlocked — edit them directly in the table.
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            border: '1.5px solid #1a4a7a',
                            background: allImportedUnlocked ? '#1a4a7a' : '#fff',
                            color: allImportedUnlocked ? '#fff' : '#1a4a7a',
                            borderRadius: 8, fontWeight: 700, fontSize: '0.76rem', padding: '3px 12px',
                          }}
                          onClick={() => {
                            if (allImportedUnlocked) {
                              setOverrideUnlockedByCat({});
                            } else {
                              const all = {};
                              importedSalesCatIds.forEach((catId) => { all[catId] = true; });
                              setOverrideUnlockedByCat(all);
                            }
                          }}
                        >
                          {allImportedUnlocked ? '🔒 Lock imported amounts' : '✏️ Edit imported amounts'}
                        </button>
                      </div>
                    )}
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.82rem', minWidth: cloverConnected ? 720 : 560 }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ minWidth: 120 }}>P&amp;L Category</th>
                            {cloverConnected && <th style={{ minWidth: 150 }}>Clover Categories Included</th>}
                            <th style={{ minWidth: 150 }}>Gross Sales</th>
                            <th className="text-end" style={{ minWidth: 95, background: '#faf6f3' }}>Net Sales</th>
                            <th style={{ minWidth: 110, whiteSpace: 'nowrap' }}>Data Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usedCalculations.map((c) => {
                            const k = String(c.catId);
                            const cloverAmt = cloverSalesData?.category_sales?.[k];
                            const posAmt = posApplied?.grossByCat?.[k];
                            // Reference import for this row — Clover wins when both exist.
                            const importedSrc = cloverAmt != null ? 'clover' : (posAmt != null ? 'pos' : null);
                            const importedAmt = cloverAmt != null ? cloverAmt : posAmt;
                            const importedLabel = importedSrc === 'clover' ? 'Clover' : 'POS report';
                            const importedRow = (grossSalesSourceByCat[k] === 'clover' && cloverAmt != null)
                              || (grossSalesSourceByCat[k] === 'pos' && posAmt != null);
                            const unlocked = !!overrideUnlockedByCat[k];
                            const manualMode = cloverConnected && salesMode === 'manual';
                            const editable = !salesUnavailable && (manualMode || !importedRow || unlocked);
                            const dedPairs = [['discounts', discountsByCat], ['comps', compsByCat], ['refunds', refundsByCat]];
                            const hasManualDeductions = dedPairs.some(([, m]) => parseOptionalAmount(m[k]) != null);
                            // Deductions the user entered beyond what the POS report supplied.
                            const posDed = posApplied?.deductionsByCat?.[k] || {};
                            const hasExtraManualDeductions = dedPairs.some(([f, m]) => {
                              const v = parseOptionalAmount(m[k]);
                              return v != null && v !== (posDed[f] ?? null);
                            });
                            const overridden = grossSalesSourceByCat[k] === 'manual' && importedAmt != null;
                            let source = '—';
                            if (!salesUnavailable && c.netSales != null) {
                              if (grossSalesSourceByCat[k] === 'clover') source = hasManualDeductions ? 'Mixed' : 'Clover';
                              else if (grossSalesSourceByCat[k] === 'pos') source = hasExtraManualDeductions ? 'Mixed' : 'POS Report';
                              else source = importedAmt != null ? 'Mixed' : 'Manual';
                            }
                            return (
                              <React.Fragment key={c.catId}>
                                <tr>
                                  <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                  {cloverConnected && (
                                    <td style={{ fontSize: '0.74rem', color: '#5A6A7A' }}>
                                      {(cloverCatNamesByPnl[k] || []).join(', ') || '—'}
                                    </td>
                                  )}
                                  <td>
                                    {salesUnavailable ? (
                                      <span style={{ color: '#A89890' }}>—</span>
                                    ) : editable ? (
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                        value={grossSalesByCat[k] ?? ''}
                                        onChange={(e) => {
                                          setGrossSalesByCat((prev) => ({ ...prev, [k]: e.target.value }));
                                          setGrossSalesSourceByCat((prev) => ({ ...prev, [k]: 'manual' }));
                                        }}
                                        onBlur={(e) => {
                                          const settled = toMoneyString(e.target.value);
                                          if (settled !== e.target.value) setGrossSalesByCat((prev) => ({ ...prev, [k]: settled }));
                                        }}
                                        aria-label={`${c.catName} gross sales`}
                                      />
                                    ) : (
                                      <span style={{ fontWeight: 600, color: '#3D2518' }}>{formatCurrency(parseNum(grossSalesByCat[k]) ?? 0)}</span>
                                    )}
                                    {overridden && (
                                      <div style={{ fontSize: '0.68rem', color: '#8B7B73', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <span>{importedLabel}: {formatCurrency(Number(importedAmt) || 0)}</span>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-link"
                                          style={{ fontSize: '0.68rem', padding: 0, color: '#1a4a7a' }}
                                          onClick={() => {
                                            setGrossSalesByCat((prev) => ({ ...prev, [k]: toMoneyString(importedAmt) }));
                                            setGrossSalesSourceByCat((prev) => ({ ...prev, [k]: importedSrc }));
                                            setOverrideUnlockedByCat((prev) => ({ ...prev, [k]: false }));
                                            setOverrideNoteByCat((prev) => ({ ...prev, [k]: '' }));
                                          }}
                                        >
                                          Reset to {importedLabel}
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                  {/* Discounts / comps / refunds are entered on the dedicated
                                      Discounts, Comps & Waste step, not previewed here. Net Sales
                                      below reflects whatever is entered there. */}
                                  <td className="text-end fw-bold" style={{ background: '#faf6f3', color: c.netSales != null && c.netSales < 0 ? '#C0392B' : '#3D2518' }}>
                                    {c.netSales != null ? formatCurrency(c.netSales) : '—'}
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <span style={{
                                      fontSize: '0.7rem', padding: '2px 8px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap',
                                      background: source === 'Clover' ? '#DDE8FA' : source === 'POS Report' ? '#E0F0E5' : source === 'Mixed' ? '#FFF3CD' : source === 'Manual' ? '#E8DDD0' : 'transparent',
                                      color: source === 'Clover' ? '#1a4a7a' : source === 'POS Report' ? '#1a5c34' : source === 'Mixed' ? '#856404' : source === 'Manual' ? '#5A4A42' : '#A89890',
                                    }}>
                                      {source}
                                    </span>
                                  </td>
                                </tr>
                                {overridden && (
                                  <tr>
                                    <td colSpan={cloverConnected ? 5 : 4} style={{ background: '#FFFDF6', padding: '0.35rem 0.6rem' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#5A4A42', whiteSpace: 'nowrap' }}>
                                          Override note (optional):
                                        </span>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder="Why is the imported amount being changed?"
                                          maxLength={240}
                                          value={overrideNoteByCat[k] || ''}
                                          onChange={(e) => setOverrideNoteByCat((prev) => ({ ...prev, [k]: e.target.value }))}
                                          style={{ flex: '1 1 260px' }}
                                          aria-label={`${c.catName} override note`}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot className="table-secondary">
                          <tr className="fw-bold">
                            <td>Totals</td>
                            {cloverConnected && <td />}
                            <td className="text-end">{totals.hasGross ? formatCurrency(totals.grossSales) : '—'}</td>
                            <td className="text-end" style={{ background: '#f3ede8' }}>
                              {totals.hasSales ? formatCurrency(totals.netSales) : '—'}
                              {totals.netSalesIncompleteCats.length > 0 && <IncompleteBadge categories={totals.netSalesIncompleteCats} />}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p style={{ fontSize: '0.74rem', color: '#8B7B73', marginTop: '0.45rem', marginBottom: 0 }}>
                      Net Sales = Gross Sales − Discounts − Comps − Refunds. Discounts, comps,
                      refunds and waste are entered on the next step (Discounts, Comps &amp; Waste);
                      Net Sales above updates from those entries.
                    </p>

                    <div className="form-check" style={{ marginTop: '0.75rem' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="eop-sales-unavailable"
                        checked={salesUnavailable}
                        onChange={(e) => setSalesUnavailable(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="eop-sales-unavailable" style={{ fontSize: '0.8rem', color: '#5A4A42' }}>
                        Sales data isn't available for this period — report inventory usage only
                      </label>
                    </div>

                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        onClick={() => goToStepKey('adjustments')}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Discounts, Comps & Waste ── */}
              {stepKey === 'adjustments' && !editingLegacyReport && (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 style={{ fontFamily: "'Montserrat', sans-serif", color: '#3D2518', fontWeight: 700 }}>
                      Discounts, Comps &amp; Waste
                    </h6>
                    <p style={{ fontSize: '0.82rem', color: '#8B7B73', marginBottom: '0.6rem' }}>
                      Review or enter operational adjustments by P&amp;L category. Amounts are the
                      retail (menu) value for the period.
                    </p>
                    <div style={{ background: '#FAF6F2', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.55rem 0.85rem', marginBottom: '0.85rem', fontSize: '0.76rem', color: '#3D2518', lineHeight: 1.5 }}>
                      <strong>How these are treated:</strong> discounts, comps, and refunds <strong>reduce
                      sales</strong> (they lower Net Sales). Waste does <strong>not</strong> reduce sales and is{' '}
                      <strong>not</strong> subtracted from Actual COGS — your counted inventory already reflects
                      what was wasted, so subtracting it again would double-count it. Waste is shown separately
                      as an operational metric.
                    </div>

                    {cloverConnected && !salesUnavailable
                      && cloverSalesData?.category_discounts
                      && Object.keys(cloverSalesData.category_discounts).length > 0 && (
                      <div style={{ background: '#E8F1FD', border: '1px solid #B9D4F2', borderRadius: 10, padding: '0.55rem 0.85rem', marginBottom: '0.85rem', fontSize: '0.76rem', color: '#1a4a7a', lineHeight: 1.5 }}>
                        <strong>Discounts are pulled from Clover</strong> and allocated by P&amp;L category — every
                        discount counts (employee, percentage, order- or item-level), and order-wide discounts are
                        split across categories in proportion to sales. They&apos;re read-only here; enter comps,
                        refunds and waste manually.
                      </div>
                    )}

                    {posApplied && (Object.keys(posApplied.unallocated).length > 0
                      || Object.keys(posApplied.deductionsByCat).length > 0
                      || posApplied.zeroConfirmed.length > 0) && (
                      <div style={{ background: '#F0FAF4', border: '1px solid #B7DCC3', borderRadius: 10, padding: '0.55rem 0.85rem', marginBottom: '0.85rem', fontSize: '0.76rem', color: '#1a5c34', lineHeight: 1.5 }}>
                        <strong>From your POS report ({posApplied.fileName}):</strong>{' '}
                        {Object.keys(posApplied.unallocated).length > 0 && (
                          posApplied.allocationChoice === 'proportional' ? (
                            <>
                              {Object.entries(posApplied.unallocated)
                                .map(([f, v]) => `${f} ${formatCurrency(v)}`)
                                .join(', ')}{' '}
                              were split across the category rows in proportion to each category's
                              share of sales — the report only gave period totals. Edit any amount
                              you know more precisely.{' '}
                            </>
                          ) : (
                            <>
                              {Object.entries(posApplied.unallocated)
                                .map(([f, v]) => `${f} ${formatCurrency(v)}`)
                                .join(', ')}{' '}
                              were placed in <em>Unallocated Adjustments</em> below — the report only gave
                              period totals, not a category split. Allocate them across the category rows,
                              or have them split in proportion to sales.{' '}
                            </>
                          )
                        )}
                        {Object.keys(posApplied.deductionsByCat).length > 0 && (
                          <>Category-level amounts from the report were filled in above and stay editable.{' '}</>
                        )}
                        {posApplied.zeroConfirmed.length > 0 && (
                          <>The report shows no {posApplied.zeroConfirmed.join(' or ')} for this period — blank fields count as $0.00.</>
                        )}
                      </div>
                    )}

                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.82rem', minWidth: 640 }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ minWidth: 130 }}>P&amp;L Category</th>
                            <th style={{ minWidth: 105 }}>Discounts ($)</th>
                            <th style={{ minWidth: 105 }}>Comps ($)</th>
                            <th style={{ minWidth: 105 }}>Refunds ($)</th>
                            <th style={{ minWidth: 105 }}>Waste ($)</th>
                            <th className="text-end" style={{ minWidth: 95, background: '#faf6f3' }}>Net Sales</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usedCalculations.map((c) => {
                            const k = String(c.catId);
                            // Discounts are pulled from Clover and shown read-only
                            // once a pull has supplied them — the operator never
                            // types discounts when the POS provides them. Comps,
                            // refunds and waste stay manual.
                            const discountsFromClover = !salesUnavailable
                              && cloverSalesData?.category_discounts?.[k] != null;
                            const cell = (value, setter, label, locked = false) => (
                              <td>
                                {locked ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <span style={{ fontWeight: 600, color: '#3D2518' }}>{formatCurrency(parseNum(value) ?? 0)}</span>
                                    <span style={{ fontSize: '0.64rem', fontWeight: 700, color: '#1a4a7a' }}>From Clover</span>
                                  </div>
                                ) : (
                                  <AmountCell
                                    value={value}
                                    onValueChange={(v) => setter((prev) => ({ ...prev, [k]: v }))}
                                    ariaLabel={`${c.catName} ${label}`}
                                  />
                                )}
                              </td>
                            );
                            return (
                              <tr key={c.catId}>
                                <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                {cell(discountsByCat[k], setDiscountsByCat, 'discounts', discountsFromClover)}
                                {cell(compsByCat[k], setCompsByCat, 'comps')}
                                {cell(refundsByCat[k], setRefundsByCat, 'refunds')}
                                {cell(wasteByCat[k], setWasteByCat, 'waste')}
                                <td className="text-end fw-bold" style={{ background: '#faf6f3', color: c.netSales != null && c.netSales < 0 ? '#C0392B' : '#3D2518' }}>
                                  {c.netSales != null ? formatCurrency(c.netSales) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Unallocated Adjustments — amounts that can't be reliably
                              assigned to a P&L category. They are saved with the
                              report and flagged in Review; allocate them across the
                              category rows above when you know where they belong. */}
                          <tr style={{ background: '#FFFDF6' }}>
                            <td className="fw-semibold" style={{ color: '#856404' }}>
                              Unallocated Adjustments
                              <div style={{ fontSize: '0.68rem', fontWeight: 400, color: '#8B7B73' }}>
                                Not assigned to a category — allocate manually above when known
                              </div>
                              {hasUnallocatedDeductions && posSalesSharesAvailable && (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  style={{ marginTop: 4, fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#5A4A42', color: '#fff', border: 'none' }}
                                  onClick={splitUnallocatedBySales}
                                >
                                  Split in proportion to sales
                                </button>
                              )}
                            </td>
                            {['discounts', 'comps', 'refunds', 'waste'].map((field) => (
                              <td key={field}>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                  value={unallocated[field] || ''}
                                  onChange={(e) => setUnallocated((prev) => ({ ...prev, [field]: e.target.value }))}
                                  onBlur={(e) => {
                                    const settled = toMoneyString(e.target.value);
                                    if (settled !== e.target.value) setUnallocated((prev) => ({ ...prev, [field]: settled }));
                                  }}
                                  aria-label={`Unallocated ${field}`}
                                />
                              </td>
                            ))}
                            <td className="text-end" style={{ color: '#A89890' }}>—</td>
                          </tr>
                        </tbody>
                        <tfoot className="table-secondary">
                          <tr className="fw-bold">
                            <td>Totals</td>
                            <td className="text-end">{formatCurrency(totals.discounts + (parseNum(unallocated.discounts) || 0))}</td>
                            <td className="text-end">{formatCurrency(totals.comps + (parseNum(unallocated.comps) || 0))}</td>
                            <td className="text-end">{formatCurrency(totals.refunds + (parseNum(unallocated.refunds) || 0))}</td>
                            <td className="text-end">{formatCurrency(totals.waste + (parseNum(unallocated.waste) || 0))}</td>
                            <td className="text-end" style={{ background: '#f3ede8' }}>{totals.hasSales ? formatCurrency(totals.netSales) : '—'}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* ── Cost allocation: discounts & comps at estimated cost ── */}
                    {!salesUnavailable && (
                      <div style={{ marginTop: '0.9rem', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.75rem 0.9rem', background: '#FFFDF9' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#3D2518', marginBottom: 4 }}>
                          Estimated Cost of Discounts &amp; Comps{' '}
                          <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#856404', background: '#FFF3CD', borderRadius: 8, padding: '1px 7px', marginLeft: 4 }}>
                            allocation of Actual COGS
                          </span>
                        </div>
                        <p style={{ fontSize: '0.74rem', color: '#8B7B73', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                          Actual COGS is calculated from beginning inventory, purchases, transfers,
                          adjustments, and ending inventory. Estimated discount and comp costs
                          allocate a portion of Actual COGS using the category's implied cost rate,
                          calculated as Actual COGS divided by Gross Sales. These allocations do
                          not change Actual COGS.
                        </p>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.78rem', minWidth: 860 }}>
                            <thead>
                              <tr>
                                <th style={{ color: '#5A4A42' }}>P&amp;L Category</th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>Actual COGS</th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>Gross Sales</th>
                                <th className="text-end" style={{ color: '#5A4A42', whiteSpace: 'nowrap' }}>
                                  Implied Actual Cost Rate
                                  <InfoTip text="Actual COGS divided by Gross Sales before discounts, comps, and refunds." />
                                </th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>Discounts at Sales Value</th>
                                <th className="text-end" style={{ color: '#5A4A42', whiteSpace: 'nowrap' }}>
                                  Estimated Cost of Discounts
                                  <InfoTip text="The estimated inventory cost associated with discounts entered at sales value." />
                                </th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>Comps at Sales Value</th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>Estimated Cost of Comps</th>
                                <th className="text-end" style={{ color: '#5A4A42' }}>COGS Attributed to Paid Sales</th>
                              </tr>
                            </thead>
                            <tbody>
                              {calculations
                                .filter((c) => Math.abs(c.actualCOGS) > 0.005 || c.grossSales != null)
                                .map((c) => {
                                  if (c.impliedCostRate == null) {
                                    return (
                                      <tr key={c.catId}>
                                        <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                        <td className="text-end">{formatCurrency(c.actualCOGS)}</td>
                                        <td colSpan={7} style={{ color: '#856404', fontSize: '0.74rem' }}>
                                          {c.allocationUnavailableReason === 'no_gross_sales'
                                            ? 'Cost allocation unavailable because gross sales were not entered for this category.'
                                            : 'Cost allocation unavailable. Complete purchases and resolve invalid or negative COGS before estimating the cost of discounts and comps.'}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  return (
                                    <tr key={c.catId}>
                                      <td className="fw-semibold" style={{ color: '#3D2518' }}>{c.catName}</td>
                                      <td className="text-end">{formatCurrency(c.actualCOGS)}</td>
                                      <td className="text-end">{formatCurrency(c.grossSales)}</td>
                                      <td className="text-end">{c.impliedCostRate}%</td>
                                      <td className="text-end">
                                        {c.discounts != null ? formatCurrency(c.discounts) : '—'}
                                      </td>
                                      <td className="text-end fw-semibold" style={{ color: '#3D2518' }}>
                                        {c.estDiscountCost != null ? formatCurrency(c.estDiscountCost) : 'Unavailable'}
                                      </td>
                                      <td className="text-end">
                                        {c.comps != null ? formatCurrency(c.comps) : '—'}
                                      </td>
                                      <td className="text-end fw-semibold" style={{ color: '#3D2518' }}>
                                        {c.estCompCost != null ? formatCurrency(c.estCompCost) : 'Unavailable'}
                                      </td>
                                      <td className="text-end fw-bold" style={{ color: '#3D2518' }}>
                                        {c.cogsPaidSales != null ? formatCurrency(c.cogsPaidSales) : 'Unavailable'}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                        {totals.refunds > 0 && (
                          <div style={{ fontSize: '0.74rem', color: '#5A4A42', marginTop: '0.4rem' }}>
                            Refunds at Sales Value: <strong>{formatCurrency(totals.refunds)}</strong> — cost impact{' '}
                            <strong>Not estimated</strong>. Refunds may or may not have consumed inventory, so no
                            cost is allocated to them until item-level refund disposition data is available.
                          </div>
                        )}
                        <div style={{ fontSize: '0.7rem', color: '#8B7B73', marginTop: '0.4rem' }}>
                          Assumes discounted and complimentary sales have approximately the same average
                          product-cost rate as the overall category — an estimated allocation, not
                          item-level recipe costing.
                        </div>
                      </div>
                    )}

                    {/* ── Waste (analytical) ── */}
                    {totals.waste > 0 && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.76rem', color: '#5A4A42', background: '#FFFDF9', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.6rem 0.9rem' }}>
                        <strong>COGS excluding separately recorded waste</strong>{' '}
                        <span style={{ fontSize: '0.66rem', fontWeight: 600, color: '#856404', background: '#FFF3CD', borderRadius: 8, padding: '1px 7px' }}>analytical</span>{' '}
                        = Actual COGS − Recorded Waste = <strong>{formatCurrency(totals.actualUsage - totals.waste)}</strong>
                        <span style={{ color: '#8B7B73' }}> — the official COGS value keeps waste in, since counted inventory already reflects it.</span>
                      </div>
                    )}
                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={goBack}>← Back</button>
                      <button
                        type="button"
                        className="btn"
                        style={{ background: '#5A4A42', color: '#fff', borderRadius: 10, fontWeight: 700, padding: '0.5rem 1.25rem', fontSize: '0.88rem', border: 'none' }}
                        onClick={() => goToStepKey('review')}
                      >
                        Review →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step: Review & Generate ── */}
              {stepKey === 'review' && !isMobile && renderReviewStep()}

              {stepKey === 'review' && isMobile && (
                <MobileReportPopup
                  title={mobileOutputTitle}
                  onClose={goBack}
                >
                  {renderReviewStep()}
                </MobileReportPopup>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════════ HISTORY TAB ════════════════ */}
      {activeTab === 'history' && (
        <div>
          {loadingHistory ? (
            <div className="text-muted small d-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" />
              Loading history…
            </div>
          ) : eopHistory.length === 0 ? (
            <div style={{
              background: '#f9f6f3', border: '1px solid #E8DDD0', borderRadius: 12,
              padding: '2rem', textAlign: 'center', color: '#8B7B73',
            }}>
              No saved End of Period reports yet. Complete your first report using the New Report tab.
            </div>
          ) : (
            <>
              {activeHistoryRow && (() => {
                const startSess = sessions.find((s) => s.id === activeHistoryRow.start_session_id);
                const endSess = sessions.find((s) => s.id === activeHistoryRow.end_session_id);
                const startDate = startSess ? formatDate(countDateFromSession(startSess)) : '';
                const endDate = endSess
                  ? formatDate(countDateFromSession(endSess))
                  : formatDate(activeHistoryRow.report_date);
                // Who ran the two inventory counts (for the "Counted By" line).
                const counters = [...new Set(
                  [startSess?.created_by, endSess?.created_by]
                    .filter(Boolean)
                    .map((uid) => userMap[uid])
                    .filter(Boolean),
                )];
                const me = getCurrentUser();
                // The admin running an EOP report in a store they don't own is
                // shown as "Countertop App" on the report's "Created By" line.
                const createdBy = (!storeOwnedBySuperAdmin && isSuperAdminId(me?.id))
                  ? COUNTERTOP_APP_CREATOR_LABEL
                  : (me?.compactName || me?.displayName || me?.email || '');
                const descriptor = buildEopReportDescriptor(activeHistoryRow, {
                  storeName: reportStoreName,
                  createdBy,
                  periodStartLabel: startDate,
                  periodEndLabel: endDate,
                  counters,
                });
                return (
                  <EopReportModal
                    descriptor={descriptor}
                    onEdit={() => { handleEditRequest(activeHistoryRow); setActiveHistoryRow(null); }}
                    onClose={() => setActiveHistoryRow(null)}
                  />
                );
              })()}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table table-sm table-hover align-middle" style={{ fontSize: '0.84rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th>Report Name</th>
                      <th>Period</th>
                      <SortableHeader label="Beginning" sortKey="beginning_total" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Ending" sortKey="ending_total" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Purchases" sortKey="purchases_total" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Actual COGS" sortKey="actual_usage_total" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Completed" sortKey="created_at" sortConfig={sortConfig} onSort={handleSort} className="text-end" />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedHistory.map((row) => {
                      const legacy = isLegacyReport(row);
                      const startSess = sessions.find((s) => s.id === row.start_session_id);
                      const endSess = sessions.find((s) => s.id === row.end_session_id);
                      const startDate = startSess ? formatDate(countDateFromSession(startSess)) : '—';
                      const endDate = endSess ? formatDate(countDateFromSession(endSess)) : formatDate(row.report_date);
                      const beginningTotal = sumReportField(row, 'beginning_inventory');
                      const endingTotal = sumReportField(row, 'ending_inventory');
                      const purchasesTotal = sumReportField(row, 'purchases');
                      const usageTotal = row.actual_usage_total ?? sumReportField(row, 'cost_of_goods_used');
                      return (
                        <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setActiveHistoryRow(row)}>
                          <td style={{ color: '#3D2518', fontWeight: 500 }}>
                            {row.period_label || `EOP ${formatDate(row.report_date)}`}
                            {legacy && (
                              <span style={{
                                marginLeft: 8, fontSize: '0.66rem', padding: '1px 7px',
                                borderRadius: 8, background: '#EAF2FB', color: '#1a4a7a', fontWeight: 700,
                              }}>
                                Legacy
                              </span>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: '#5A4A42' }}>{startDate} → {endDate}</td>
                          <td className="text-end">{formatCurrency(beginningTotal)}</td>
                          <td className="text-end">{formatCurrency(endingTotal)}</td>
                          <td className="text-end">{formatCurrency(purchasesTotal)}</td>
                          <td className="text-end fw-semibold">{formatCurrency(usageTotal)}</td>
                          <td className="text-end" style={{ whiteSpace: 'nowrap', color: '#8B7B73' }}>
                            {formatDate(row.created_at)}{formatTime12h(row.created_at) ? ` · ${formatTime12h(row.created_at)}` : ''}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ fontSize: '0.75rem', padding: '0.15rem 0.55rem' }}
                              onClick={(e) => { e.stopPropagation(); handleEditRequest(row); }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EOPWizard;
